// Thin wrapper over the matrix-rust-sdk WASM bindings.
//
// We target `matrix-rust-sdk` (Rust → wasm-bindgen) rather than
// `matrix-js-sdk`. The full-client bindings are pre-1.0 and not yet on
// npm — the crypto layer is, and we'll use that shortly. The README
// records the pinned commit and the integration steps for swapping the
// runtime in. Until then this file exposes a typed surface that mirrors
// the shape we'll get from the SDK, backed by a documented stub. The
// stub is the "emergency fallback" the ticket describes: when the WASM
// bindings land, only the SdkBindings implementation changes.
//
// Anything outside this module sees only the wrapper API below.

import {
  deleteChatIndexedDbDatabases,
  MATRIX_SESSION_STORE_PREFIX,
  platform,
  readSecret,
} from "~/lib/platform";
import type { ChannelType } from "~/lib/channel-type";
import { replacementBody } from "~/lib/message-utils";
import { hasRichThreadOptions } from "~/lib/thread-reply-content";

export type ClientState = "loading" | "login" | "syncing" | "synced" | "reconnecting" | "error";

/**
 * Payload emitted by the Tauri `oidc-callback` deep-link listener and
 * consumed by `MatrixContext.completeLogin`. The previous SSO loginToken
 * shape was retired in #3156 — `callbackQuery` is the raw query string
 * of the redirect URI (everything after the leading `?`); the SDK
 * parses it and validates `state` internally.
 */
export type OidcCallbackPayload = { callbackQuery: string };

const SESSION_CHANNEL_NAME = "nurion-chat-session";
let sessionChannel: BroadcastChannel | null = null;

function getSessionChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (sessionChannel) return sessionChannel;
  sessionChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
  return sessionChannel;
}

export type SessionBroadcastMessage = { type: "signOut"; at: number };

export function notifySignOutBroadcast(): void {
  getSessionChannel()?.postMessage({
    type: "signOut",
    at: Date.now(),
  } satisfies SessionBroadcastMessage);
}

export function subscribeSessionBroadcast(
  handler: (msg: SessionBroadcastMessage) => void,
): () => void {
  const channel = getSessionChannel();
  if (!channel) return () => {};
  const listener = (ev: MessageEvent<unknown>) => {
    const data = ev.data;
    if (!data || typeof data !== "object") return;
    if (!("type" in data) || data.type !== "signOut") return;
    const at = "at" in data && typeof data.at === "number" ? data.at : Date.now();
    handler({ type: "signOut", at });
  };
  channel.addEventListener("message", listener);
  return () => channel.removeEventListener("message", listener);
}

/* ── Crypto / E2EE ──────────────────────────────────────────────── */

/**
 * Per-message encryption status the UI renders a shield for.
 *
 * `verified`      — decrypted from a cross-signed device.
 * `unverified`    — decrypted, but the sending device isn't cross-signed
 *                   yet. The message is still readable.
 * `undecryptable` — "Unable to decrypt": we lack the session key. The
 *                   UI shows a placeholder body with a "Request keys"
 *                   action that calls `requestKeysForEvent`.
 * `unencrypted`   — sent to an unencrypted room (or a state event that
 *                   by spec is never encrypted). No shield rendered.
 */
export type MessageEncryptionStatus = "unencrypted" | "verified" | "unverified" | "undecryptable";

export interface EncryptionInfoDto {
  algorithm: string;
  senderDevice?: string;
  senderCurve25519Key: string;
  senderClaimedKeys: Record<string, string>;
  verificationState: "verified" | "unverified" | "tofu";
  sessionId?: string;
}

/**
 * Minimal structural shape of a timeline event the UI needs to classify.
 * The real SDK returns `MatrixEvent`; we accept anything with these
 * discriminators so tests and stories can build fixture events without
 * importing the SDK.
 */
export interface MessageEventLike {
  isEncrypted?: boolean;
  isDecryptionFailure?: boolean;
  senderDeviceVerified?: boolean;
  encryptionStatus?: MessageEncryptionStatus;
}

/**
 * Matrix user-interactive-auth payload. Surfaced to the app layer so an
 * interactive-auth handler can present a password / SSO flow and return
 * the continuation token. We keep the shape loose — the caller must
 * handle the specific `flows` / `params` presented by the homeserver.
 */
export interface IAuthData {
  session?: string;
  flows?: Array<{ stages: string[] }>;
  params?: Record<string, unknown>;
  completed?: string[];
  /** The token / payload the caller assembled for the next stage. */
  auth?: Record<string, unknown> | null;
}

export type InteractiveAuthCallback = (req: IAuthData) => Promise<IAuthData>;

export type VerificationState =
  | "requested"
  | "ready"
  | "accepted"
  | "done"
  | "cancelled"
  | "failed";

/** One cell in the SAS emoji grid. The real SDK returns the same shape
 *  (a short emoji glyph + English label). */
export interface VerificationEmoji {
  symbol: string;
  label: string;
}

/**
 * A live SAS (short-authentication-string) verification session. One
 * side per end: both sides observe identical emoji, the user confirms
 * they match, and the session transitions to `done`.
 *
 * Sessions are terminal — `confirm` / `cancel` each end the session.
 * State change callbacks stop firing after `done` / `cancelled` /
 * `failed`, and subscribers are free to release their reference then.
 */
export interface VerificationSession {
  /** The 7 SAS emoji both sides should compare. */
  readonly emoji: readonly VerificationEmoji[];
  /** Other user's matrix id. Useful for banner / dialog copy. */
  readonly otherUserId: string;
  readonly otherDeviceId: string;
  /** Confirm "they match" — completes the session on both sides. */
  confirm(): Promise<void>;
  /** Cancel — signals "they don't match" or the user closed the dialog. */
  cancel(): Promise<void>;
  /** Current state. Useful for stories / tests that render a pinned
   *  state without subscribing to transitions. */
  readonly state: VerificationState;
  /** Subscribe to state changes. Fires once synchronously with the
   *  current state and then on every transition. Returns an unsubscribe
   *  function. */
  onState(cb: (s: VerificationState) => void): Unsubscribe;
}

export interface QrVerificationSession {
  readonly qrCodeData: Uint8Array | null;
  readonly otherUserId: string;
  readonly otherDeviceId: string;
  readonly state: VerificationState;
  readonly weStarted: boolean;
  confirm(): Promise<void>;
  cancel(): Promise<void>;
  onState(cb: (s: VerificationState) => void): Unsubscribe;
}

/**
 * An incoming verification request. The UI surfaces these as a banner
 * at the top of the shell; clicking "Verify" promotes the request to a
 * live `VerificationSession` via `acceptSas`.
 */
export interface VerificationRequest {
  readonly id: string;
  readonly otherUserId: string;
  readonly otherDeviceId: string;
  readonly state: VerificationState;
  /** Display name of the other side, if the SDK already has it. The
   *  banner falls back to `otherUserId` when null. */
  readonly otherDisplayName: string | null;
  accept(): Promise<VerificationRequest>;
  /** Accept the request and start an SAS exchange. Resolves with the
   *  live session the dialog should render. */
  acceptSas(): Promise<VerificationSession>;
  startSas(): Promise<VerificationSession | null>;
  generateQrCode(): Promise<QrVerificationSession | null>;
  scanQrCode(bytes: Uint8Array): Promise<QrVerificationSession | null>;
  /** The live SAS session a peer-initiated request transitioned into, or
   *  `null` until it transitions or when this side started the flow. */
  peerSasSession(): Promise<VerificationSession | null>;
  /** QR counterpart of `peerSasSession`. */
  peerQrSession(): Promise<QrVerificationSession | null>;
  /** Decline the request. Closes the banner. */
  cancel(): Promise<void>;
}

export interface MatrixDevice {
  deviceId: string;
  displayName?: string | null;
  isVerified: boolean;
  isCrossSignedByOwner: boolean;
  hasIdentityKey?: boolean;
  isDehydrated?: boolean;
  lastSeenTs?: number | null;
}

export interface RecoveryDevice extends MatrixDevice {
  label: string;
}

export type RecoveryPrimaryAction =
  | { kind: "verify-device"; device: RecoveryDevice }
  | { kind: "recovery-key" };

export interface RecoveryViewModel {
  primaryAction: RecoveryPrimaryAction;
  devices: RecoveryDevice[];
  staleDevices: RecoveryDevice[];
  canShowQr: boolean;
  canUseEmoji: boolean;
  skipped: boolean;
}

export interface VerifiedDeviceRestoreResult {
  restoredCount: number | null;
}

/** Matrix-standard room state event for pinned timeline events. */
export const PINNED_EVENTS_TYPE = "m.room.pinned_events" as const;

/** Custom room state event used to archive channels without deleting history. */
export const ARCHIVED_EVENT_TYPE = "com.nurion.archived" as const;

/** Matrix-standard room power-level state event. */
export const POWER_LEVELS_EVENT_TYPE = "m.room.power_levels" as const;

export interface ArchivedStateContent {
  archivedAt: number;
  archivedBy: string;
  reason?: string;
  previousEventsDefault?: number;
  previousMessageEventLevel?: number | null;
}

interface PowerLevelsStateContent {
  users?: Record<string, number | string>;
  users_default?: number | string;
  events?: Record<string, number | string>;
  events_default?: number | string;
  state_default?: number | string;
  ban?: number | string;
  kick?: number | string;
  redact?: number | string;
  invite?: number | string;
  notifications?: Record<string, number | string>;
}

/** Shape of `m.room.pinned_events` state event content. */
export interface PinnedEventsStateContent {
  /** Ordered event ids. Newest-first is the UI convention for Chat. */
  pinned: string[];
}
/**
 * Record of the local user being removed from a room by someone else
 * (`m.room.member` state event with `state_key === userId`). Distinct
 * from a voluntary leave: the UI surfaces who did it and why, keeps the
 * room visible/readable until the user dismisses it, and disables the
 * composer. Cleared when the user rejoins.
 */
export type RemovedBySelf = {
  /** `"kicked"` for `membership: "leave"`, `"banned"` for `"ban"`. */
  kind: "kicked" | "banned";
  /** Id of the member who performed the removal (event sender). */
  byUserId: string;
  /** Display name of the actor at the time of removal, if known. */
  byDisplayName: string | null;
  /** Kick/ban reason from `content.reason`, or `null` when unset. */
  reason: string | null;
};

/**
 * A summary of a joined Matrix room surfaced to the UI layer.
 *
 * Every field is a projection of state the SDK already holds — we
 * intentionally keep this flat so a single `subscribeRooms` snapshot is
 * enough to paint the whole sidebar. No hook needs to fetch anything
 * further; the `MatrixContext` + subscription is the single source of
 * truth.
 *
 */
export interface RoomSummary {
  id: string;
  name: string;
  /** Room topic, when set. Surfaces in the timeline header; kept here
   *  so every consumer reads one shape. */
  topic: string | null;
  /** Resolved `mxc://` avatar URL (after the SDK converts to an https
   *  thumbnail). `null` when the room has no avatar — consumers render
   *  an initials fallback. */
  avatarUrl: string | null;
  /** Joined members only. Invited members are deliberately excluded —
   *  the brief's member count matches what a user sees in the header. */
  memberCount: number;
  /** Milliseconds since epoch of the last message-like event. `0` for
   *  rooms that have never received one (rare; usually means the
   *  account joined but the timeline hasn't loaded yet). */
  lastActivityTs: number;
  /** Push-rule-derived unread total. Server-computed via
   *  `unread_notifications.notification_count`. */
  unreadTotal: number;
  /** Highlight (mention) count, a subset of `unreadTotal`. */
  unreadHighlight: number;
  isDirect: boolean;
  /** List of Matrix user ids this is a direct message with (empty for channels). */
  directUserIds: string[];
  /** Current value of the `com.nurion.channel_type` state event. `null`
   *  if the room has no event set yet — the UI should treat `null` as
   *  the implicit `mixed` default but keep the distinction so we never
   *  render a "mixed" glyph where the event is genuinely absent. */
  channelType: ChannelType | null;
  /** Active archive marker. `null` means the room is not archived. */
  archived: ArchivedStateContent | null;
  /** Set when the local user was kicked or banned from this room by
   *  another member. Absent/undefined while the user is still in the
   *  room; kept until the user rejoins so the removed notice survives a
   *  sync that otherwise drops the room. */
  removedBySelf?: RemovedBySelf;
  /** Whether the local user's power level allows writing the
   *  `com.nurion.channel_type` state event. Computed from the room's
   *  `m.room.power_levels` event. The "Take over" affordance is
   *  disabled when this is false so we don't surface an action the
   *  server will reject. */
  canSetChannelType: boolean;
  /** True iff the Matrix `m.room.create` event marks this room as a
   *  Space (`type: "m.space"`). Spaces are surfaced as sidebar
   *  sections, not rows, so the sidebar filter excludes them. */
  isSpace: boolean;
  /** Joined room ids the user has visible as children of this Space
   *  via `m.space.child` state events. `null` for non-Space rooms.
   *  An empty array is meaningful: it's a Space with no joined
   *  children, which the sidebar hides. */
  spaceChildRoomIds: string[] | null;
}

export interface InvitedRoom {
  roomId: string;
  name: string | null;
  topic: string | null;
  inviter: string;
  isDirect: boolean;
  encrypted: boolean;
}

export type RoomsSubscriber = (rooms: RoomSummary[]) => void;
export type InvitedRoomsSubscriber = (invites: InvitedRoom[]) => void;
export type AccountDataSubscriber<T> = (content: T | null) => void;
export type RoomStateSubscriber<T> = (content: T | null) => void;
export type Unsubscribe = () => void;
export type ReadMarkerSubscriber = (eventId: string | null) => void;

/* ── Mentions activity feed ─────────────────────────────────────── */

/** One row in the Mentions activity view. */
export type MentionFeedItem =
  | {
      kind: "mention";
      roomId: string;
      roomName: string;
      eventId: string;
      body: string;
      mentionToken: string | null;
      sender: { id: string; displayName: string; avatarUrl: string | null };
      ts: number;
    }
  | {
      kind: "reaction";
      roomId: string;
      roomName: string;
      eventId: string;
      targetEventId: string;
      emoji: string;
      sender: { id: string; displayName: string; avatarUrl: string | null };
      ts: number;
    };

export type MentionsFeedSubscriber = (items: MentionFeedItem[]) => void;

/* ── Push rules / notifications ──────────────────────────────────
 *
 * Matrix push rules are the server-side policy engine that decides
 * whether any given event "should make noise" on a given account. The
 * client does not re-implement matching — it asks the SDK what the
 * homeserver-evaluated actions are for a rendered event and renders a
 * desktop notification when the result says so.
 *
 * Per-room overrides are stored server-side as `room`-kind rules with
 * the `room_id` as the rule id. Three UI levels map to three server
 * states:
 *
 *   all       → override with `notify`
 *   mentions  → no override (default behaviour, other rules still apply)
 *   mute      → override with `dont_notify`
 */

/**
 * Actions the homeserver said apply to a given event. `notify` is the
 * "make noise" decision after rule evaluation; `highlight` is the
 * "this is a mention" decision (drives the tab-title `(N)` count).
 *
 * Returns `null` when the event does not exist on the client or push
 * rules have not been evaluated yet — callers should skip the
 * notification rather than fall back to any default.
 */
export interface PushActions {
  notify: boolean;
  highlight: boolean;
}

/**
 * UI-facing per-room notification level. A thin projection of Matrix
 * push-rule state — the three levels the NotificationSettings popover
 * offers. Mapping to/from the underlying rule is owned by the
 * bindings.
 */
export type RoomNotificationLevel = "all" | "mentions" | "mute";

/** Aggregate unread / highlight counts across every joined room. */
export interface UnreadCounts {
  total: number;
  highlight: number;
}

export type UnreadCountsSubscriber = (counts: UnreadCounts) => void;

/* ── Presence / typing / receipts ────────────────────────────────── */

/**
 * Matrix-standard presence states. `unavailable` is Matrix's
 * wire-format name for "away"; we keep the spec vocabulary at the
 * SDK seam and translate to design-brief language (`away`) only at
 * the token layer where it matters for colour.
 */
export type PresenceState = "online" | "unavailable" | "offline";

/**
 * Live presence snapshot for a single user. `lastActive` is a
 * Matrix `last_active_ago`-derived ms-since-epoch timestamp, or null
 * when the server hasn't surfaced one yet (e.g. a user we haven't
 * seen in /sync).
 */
export interface PresenceSnapshot {
  state: PresenceState;
  lastActive: number | null;
  /** Matrix presence `status_msg`, when the homeserver surfaced one. */
  statusMsg?: string | null;
}

export type PresenceSubscriber = (snapshot: PresenceSnapshot) => void;

export interface UserProfile {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface OpenIdToken {
  accessToken: string;
  expiresIn: number;
  serverName: string;
}

export interface UserStatusAccountData {
  emoji?: string;
  message?: string;
  statusMsg?: string;
  expiresAt?: number | null;
  meetingUrl?: string | null;
}

export interface UserStatus {
  emoji: string;
  message: string;
  expiresAt: number | null;
}

export const USER_STATUS_EVENT_TYPE = "com.nurion.status" as const;

export const MEETING_ACCOUNT_DATA_TYPE = "com.nurion.meeting" as const;
export const MEETING_ACTIVE_STATE_TYPE = "com.nurion.meeting_active" as const;

export interface MeetingPresence {
  meetingId: string;
  title: string;
  startedAt: string;
  /** Base (capability-less) join URL. The per-user join URL is minted
   *  on click via booking's join-token endpoint. */
  joinUrl: string;
  matrixRoomId: string;
  /** Member-only-readable join capability; possession proves room
   *  membership and is relayed to booking to mint a per-user URL. */
  joinCapability: string;
  hostUserId?: string;
  active: boolean;
}

export interface UserTimezoneAccountData {
  timezone: string;
}

export type DndDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface DndScheduleWindow {
  days: DndDay[];
  start: string;
  end: string;
}

export interface DndAccountData {
  manual: { until: number | null };
  schedule: {
    enabled: boolean;
    windows: DndScheduleWindow[];
  };
}

/** One user's read-receipt for a specific event. `ts` is the
 *  homeserver-supplied receipt timestamp (origin_server_ts on the
 *  receipt event). */
export interface Receipt {
  userId: string;
  ts: number;
}

export type TypingSubscriber = (userIds: string[]) => void;
export type ReceiptsSubscriber = () => void;

/* ── Timeline types ─────────────────────────────────────────────── */

/** Delivery status of an event as the UI needs to render it. */
export type TimelineEventStatus = "sent" | "sending" | "failed";

/**
 * Tagged union of content payloads. The wrapper enumerates the message
 * bubble types and the state events the timeline can surface as compact
 * rows. Other events surface as a generic `m.state` row carrying their
 * raw Matrix event type and content.
 */
export type TimelineEventContent =
  | {
      type: "m.text" | "m.notice" | "m.emote";
      body: string;
      format?: string | null;
      formattedBody?: string | null;
      mentions?: { userIds: string[]; room: boolean } | null;
      newContent?: TimelineEventContent;
      "com.nurion.actions"?: unknown;
    }
  | {
      type: "m.image";
      body: string;
      url: string;
      source?: MatrixMediaSource;
      /** Matrix `info.thumbnail_url` when the sender attached one.
       *  Mirrors the `m.image` spec — the UI prefers the thumbnail for
       *  inline previews and falls back to `url` only when absent. */
      thumbnailUrl?: string;
      thumbnailSource?: MatrixMediaSource;
      mimetype?: string;
      width?: number;
      height?: number;
      size?: number;
      blurhash?: string | null;
      blurhashLegacy?: string | null;
      thumbnailInfo?: {
        w?: number | null;
        h?: number | null;
        mimetype?: string | null;
        size?: number | null;
      } | null;
      newContent?: TimelineEventContent | null;
    }
  | {
      type: "m.video";
      body: string;
      url: string;
      source?: MatrixMediaSource;
      thumbnailUrl?: string;
      thumbnailSource?: MatrixMediaSource;
      mimetype?: string;
      size?: number;
      width?: number;
      height?: number;
      duration?: number;
      thumbnailInfo?: {
        w?: number | null;
        h?: number | null;
        mimetype?: string | null;
        size?: number | null;
      } | null;
      newContent?: TimelineEventContent | null;
    }
  | {
      type: "m.file";
      body: string;
      url: string;
      source?: MatrixMediaSource;
      thumbnailUrl?: string;
      thumbnailSource?: MatrixMediaSource;
      mimetype?: string;
      size?: number;
      filename?: string | null;
      caption?: string | null;
      newContent?: TimelineEventContent | null;
    }
  | {
      type: "m.audio";
      body: string;
      url: string;
      source?: MatrixMediaSource;
      mimetype?: string;
      size?: number;
      duration?: number;
      waveform?: number[];
      voice?: boolean | null;
      newContent?: TimelineEventContent | null;
    }
  | {
      type: "m.location";
      body: string;
      geoUri?: string;
      description?: string;
      asset?: "m.self" | "m.pin";
      ts?: number;
      newContent?: TimelineEventContent | null;
    }
  | { type: "m.reaction"; key?: string }
  | { type: "m.room.redaction"; reason?: string }
  | {
      type: "m.room.encrypted";
      algorithm?: string | null;
      senderKey?: string | null;
      sessionId?: string | null;
      deviceId?: string | null;
    }
  | {
      type: "m.redacted";
      originalType: string;
      originalMsgtype?: string | null;
      reason?: string | null;
      redactedBy?: string | null;
    }
  | {
      type: "m.room.member";
      membership: "join" | "leave" | "invite" | "ban" | "knock";
      displayName?: string | null;
      avatarUrl?: string | null;
      reason?: string | null;
      isDirect?: boolean | null;
      thirdPartyInvite?: { display_name?: string; signed: unknown } | null;
      prevContent?: {
        displayName?: string | null;
        avatarUrl?: string | null;
        membership?: string | null;
      } | null;
    }
  | { type: "m.room.member"; membership: null; raw: unknown }
  | { type: "m.room.name"; name: string }
  | { type: "m.room.topic"; topic: string }
  | {
      type: "m.room.power_levels";
      users?: Record<string, number>;
      usersDefault?: number;
      events?: Record<string, number>;
      eventsDefault?: number;
      stateDefault?: number;
      ban?: number;
      kick?: number;
      redact?: number;
      invite?: number;
      notifications?: Record<string, number>;
      prevUsers?: Record<string, number>;
    }
  | {
      type: "m.room.tombstone";
      body: string;
      replacementRoom?: string;
    }
  | {
      type: "m.room.canonical_alias";
      alias?: string;
      altAliases?: string[];
    }
  | {
      type: "m.room.history_visibility";
      historyVisibility?: "invited" | "joined" | "shared" | "world_readable";
    }
  | {
      type: "m.room.join_rules";
      joinRule?: "public" | "invite" | "knock" | "restricted" | "knock_restricted" | "private";
      allow?: unknown;
    }
  | {
      type: "m.room.guest_access";
      guestAccess?: "can_join" | "forbidden";
    }
  | {
      type: "m.room.avatar";
      url?: string;
      info?: { mimetype?: string; w?: number; h?: number; size?: number };
    }
  | {
      type: "m.room.create";
      creator?: string;
      roomVersion?: string;
      predecessor?: { roomId: string; eventId: string };
      roomType?: string;
    }
  | {
      type: "m.space.child";
      via?: string[];
      order?: string;
      suggested?: boolean;
    }
  | {
      type: "m.space.parent";
      via?: string[];
      canonical?: boolean;
    }
  | {
      type: "com.nurion.channel_type";
      content: unknown;
    }
  | {
      type: "com.nurion.archived";
      content: unknown;
    }
  | { type: "m.state"; eventType: string; content: unknown }
  // Decryption failed inside the encrypted envelope — original
  // msgtype unknown. Rendered as a system row.
  | { type: "m.undecryptable" }
  // `m.room.message` whose `msgtype` the UI does not render. The
  // sender-authored `body` is dropped; only the wire-level msgtype
  // rides along as metadata.
  | { type: "m.unsupported"; msgtype: string };

export type TimelineEventType = TimelineEventContent["type"];

export type MatrixMediaSource = { url: string } | { file: unknown };

export interface MatrixMediaBytes {
  bytes: Uint8Array;
}

/** Where a media message goes when it is a thread reply. */
export interface MediaThreadTarget {
  rootId: string;
  /** The thread's latest reply, or the root when it has none. */
  latestReplyId: string;
}

export type SendImageMessage = {
  data: Blob;
  w?: number;
  h?: number;
  size: number;
  mimetype: string;
  filename: string;
  thread?: MediaThreadTarget;
} & (
  | { thumbnailData?: undefined; thumbnailWidth?: undefined; thumbnailHeight?: undefined }
  | { thumbnailData: Blob; thumbnailWidth: number; thumbnailHeight: number }
);

export interface SendBinaryMediaMessage {
  data: Blob;
  size: number;
  mimetype: string;
  filename: string;
  thread?: MediaThreadTarget;
}

export type SendVideoMessage = SendBinaryMediaMessage & {
  width?: number;
  height?: number;
  duration?: number;
} & (
    | { thumbnailData?: undefined; thumbnailWidth?: undefined; thumbnailHeight?: undefined }
    | { thumbnailData: Blob; thumbnailWidth: number; thumbnailHeight: number }
  );

/* ── Threads ─────────────────────────────────────────────────────
 *
 * We use the stable `m.thread` relation (MSC3440). Every thread reply
 * carries:
 *
 *   m.relates_to: {
 *     rel_type: "m.thread",
 *     event_id: "<thread-root event_id>",
 *     is_falling_back: false,
 *     "m.in_reply_to": { event_id: "<latest reply id or root id>" }
 *   }
 *
 * Thread-aware clients (us, Element, etc.) file the event into the
 * thread timeline and filter it out of the main timeline via
 * `isThreadReply`.
 */

export const THREAD_REL_TYPE = "m.thread" as const;

export interface ThreadReplyOptions {
  formattedBody?: string;
  mentions?: string[];
}

/**
 * Minimum content shape required to detect a thread relation on an
 * event. Additional fields are allowed and ignored — we only read what
 * we need.
 */
export interface ThreadRelation {
  rel_type: typeof THREAD_REL_TYPE;
  event_id: string;
  is_falling_back?: boolean;
  "m.in_reply_to"?: { event_id: string };
}

/**
 * Aggregate "this event has been replied to in a thread" signal — the
 * homeserver-bundled `m.relations` aggregation projected onto the
 * wrapper. Present on thread-root events whose thread carries at least
 * one reply; absent (or `replyCount === 0`) for everything else.
 *
 * Boundary contract: real Matrix-JS-SDK populates this from the
 * homeserver's bundled `m.relations.m.thread` aggregation on each
 * event (Synapse computes it server-side and ships it on the
 * envelope). The wrapper exposes the reply count and latest reply
 * timestamp needed by the inline preview and cross-room Threads feed.
 *
 * Optional shape: events outside `threadReplies` (the stub) or
 * without a server-bundled aggregation (real SDK) omit it. Callers
 * read `event.threadSummary?.replyCount ?? 0` to detect "is this a
 * thread root?" — a missing summary means "no thread known", not
 * "thread known to be empty".
 */
export interface TimelineEventThreadSummary {
  replyCount: number;
  latestReplyTs?: number;
}

export interface ThreadRootsPage {
  roots: TimelineEvent[];
  nextBatch: string | null;
}

export type UtdReason =
  | "unknown"
  | "malformed_event"
  | "missing_session"
  | "unknown_message_index"
  | "decryption_failure"
  | "payload_invalid"
  | "mismatched_identity_keys"
  | "sender_not_trusted";

/**
 * A single timeline event. `content.type` is the discriminator for the
 * UI renderer; `type` on the envelope mirrors it so callers can filter
 * without narrowing the union.
 *
 * `ts` is a Matrix `origin_server_ts` (ms since epoch). Local-echo
 * events use the client's `Date.now()` — close enough for grouping.
 *
 * `threadRelation` is present when the event is a thread reply (i.e.
 * carries an `m.thread` relation). Thread-aware code filters it out
 * of the main timeline via `isThreadReply`.
 *
 * `threadSummary` is present on thread-root events whose thread has
 * at least one reply; see `TimelineEventThreadSummary` for the
 * boundary contract.
 */
export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  sender: { id: string; displayName: string; avatarUrl: string | null };
  ts: number;
  content: TimelineEventContent;
  status: TimelineEventStatus;
  /** Per-event encryption state. Omitted means an unencrypted event. */
  encryptionStatus?: MessageEncryptionStatus;
  /** Rich decryption provenance for successfully decrypted encrypted events. */
  encryptionInfo?: EncryptionInfoDto;
  utdReason?: UtdReason;
  utdWithheldCode?: string;
  /** True when the event has been redacted but remains as a placeholder row. */
  redacted?: boolean;
  /** Timestamp of the latest applied `m.replace` edit. */
  editedAt?: number;
  /** Aggregated `m.reaction` annotations for this event. */
  reactions?: ReactionSummary[];
  actionResponses?: ActionResponse[];
  /** Thread relation if present. Absent (`null` or omitted) for
   *  top-level timeline events. */
  threadRelation?: ThreadRelation | null;
  /** Bundled thread aggregation when this event is a thread root with
   *  at least one reply. Optional — missing means "no thread known". */
  threadSummary?: TimelineEventThreadSummary;
  /** The local echo id this event replaced once the server accepted it,
   *  so the row keeps its identity (and its place) through the rename. */
  localEchoId?: string;
  /** Upload progress of a media message still on its way, 0–100. */
  uploadProgress?: number;
}

export type TimelineSubscriber = (events: TimelineEvent[]) => void;
export interface TimelineSubscriptionOptions {
  cache?: boolean;
}

export type ThreadSubscriber = (replies: TimelineEvent[], meta?: { atStart?: boolean }) => void;

/**
 * True when the event is a thread reply (i.e. carries an `m.thread`
 * relation). The main `Timeline` filters these out so thread traffic
 * does not leak into the room feed.
 */
export function isThreadReply(event: TimelineEvent | null | undefined): boolean {
  // Defensive optional chain: callers commonly filter sparse arrays or
  // pass the result of `find` — returning `false` for `null`/`undefined`
  // is the honest answer and avoids a crash at the call site.
  return event?.threadRelation?.rel_type === THREAD_REL_TYPE;
}

/**
 * Summary of a thread used to render the inline preview under a root
 * message. `lastReply` is `null` when a thread exists without any
 * surfaced reply yet (e.g. replies still paginating in) — callers
 * should hide the preview in that case.
 */
export interface ThreadSummary {
  replyCount: number;
  lastReply: TimelineEvent | null;
  /** Up to the three most recent distinct senders of this thread, in
   *  most-recent-first order. Used to render the avatar stack. */
  recentSenders: Array<{ id: string; avatarUrl: string | null }>;
}

/**
 * Compute a `ThreadSummary` from an ordered list of replies
 * (chronological oldest → newest). Pure — callers pass whatever the
 * subscriber last emitted.
 */
export function summarizeThread(replies: TimelineEvent[]): ThreadSummary {
  if (replies.length === 0) {
    return { replyCount: 0, lastReply: null, recentSenders: [] };
  }
  const lastReply = replies[replies.length - 1] ?? null;
  // Walk newest → oldest collecting distinct senders, capped at 3.
  // Full optional chaining on `sender` guards against malformed
  // events where the `sender` object is missing (stub paths, partial
  // sync payloads) — the walk should skip them, not throw.
  const seen: Array<{ id: string; avatarUrl: string | null }> = [];
  for (let i = replies.length - 1; i >= 0 && seen.length < 3; i -= 1) {
    const sender = replies[i]?.sender;
    if (sender?.id && !seen.some((s) => s.id === sender.id)) {
      seen.push({ id: sender.id, avatarUrl: sender.avatarUrl });
    }
  }
  return {
    replyCount: replies.filter((reply) => !reply.redacted).length,
    lastReply,
    recentSenders: seen,
  };
}

/** One row in the Threads activity feed. */
export interface ThreadsFeedItem {
  roomId: string;
  roomName: string;
  rootEventId: string;
  rootSender: string;
  rootBodyPreview: string;
  lastReplySender: string;
  lastReplyBodyPreview: string;
  lastReplyEventId: string;
  lastReplyTs: number;
  replyCount: number;
  unreadReplyCount: number;
  unreadReplyEventIds?: string[];
  /** Full root event kept for opening ThreadPanel without a lookup. */
  rootEvent: TimelineEvent;
}

export interface ThreadsFeedMeta {
  settled: boolean;
}

export type ThreadsFeedSubscriber = (items: ThreadsFeedItem[], meta: ThreadsFeedMeta) => void;

/* ── Report and block ───────────────────────────────────────────── */

export const REPORT_REASONS = [
  "Spam",
  "Harassment",
  "Inappropriate content",
  "Something else",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const REPORT_NOTE_MAX = 280;

/** Reports a message to the homeserver admins as "{reason}: {note}". */
export async function reportEvent(
  client: Client,
  roomId: string,
  eventId: string,
  reason: ReportReason,
  note = "",
): Promise<void> {
  const trimmed = note.trim().slice(0, REPORT_NOTE_MAX);
  await client.bindings.reportEvent(roomId, eventId, trimmed ? `${reason}: ${trimmed}` : reason);
}

/** Agent users are provisioned as `@_agent_*` by matrix-agent-gateway. */
export function isAgentUserId(userId: string): boolean {
  return userId.startsWith("@_agent_");
}

/** Your own messages and an agent's replies in a 1:1 with it aren't reportable. */
export function canReportMessage(
  senderId: string,
  ownUserId: string | null,
  room: Pick<RoomSummary, "isDirect">,
): boolean {
  if (senderId === ownUserId) return false;
  return !(room.isDirect && isAgentUserId(senderId));
}

/** Matrix's block list: the homeserver stops delivering these users' events. */
export const IGNORED_USER_LIST_TYPE = "m.ignored_user_list" as const;

interface IgnoredUserListContent {
  ignored_users: Record<string, Record<string, never>>;
}

function ignoredUserIds(raw: unknown): string[] {
  if (!raw || typeof raw !== "object") return [];
  const users = (raw as { ignored_users?: unknown }).ignored_users;
  if (!users || typeof users !== "object") return [];
  return Object.keys(users);
}

export function subscribeBlockedUsers(
  client: Client,
  cb: (userIds: string[]) => void,
): Unsubscribe {
  return client.bindings.subscribeAccountData<unknown>(IGNORED_USER_LIST_TYPE, (raw) =>
    cb(ignoredUserIds(raw)),
  );
}

/** Drops 1:1 DMs with blocked people; the rooms stay joined server-side. */
export function withoutBlockedDirects(
  rooms: RoomSummary[],
  blockedUserIds: string[],
): RoomSummary[] {
  if (blockedUserIds.length === 0) return rooms;
  const blocked = new Set(blockedUserIds);
  return rooms.filter(
    (room) =>
      !(room.isDirect && room.directUserIds.length === 1 && blocked.has(room.directUserIds[0]!)),
  );
}

export async function setUserBlocked(
  client: Client,
  userId: string,
  blocked: boolean,
): Promise<void> {
  const current = new Set(
    ignoredUserIds(await client.bindings.getAccountData<unknown>(IGNORED_USER_LIST_TYPE)),
  );
  if (blocked === current.has(userId)) return;
  if (blocked) current.add(userId);
  else current.delete(userId);
  const content: IgnoredUserListContent = {
    ignored_users: Object.fromEntries([...current].map((id) => [id, {}])),
  };
  await client.bindings.setAccountData<IgnoredUserListContent>(IGNORED_USER_LIST_TYPE, content);
}

/* ── Link previews ──────────────────────────────────────────────── */

/**
 * Wrapper-side shape of a Synapse `/_matrix/media/v3/preview_url`
 * response. Synapse mirrors the OpenGraph vocabulary, so the raw
 * payload uses `og:title` / `og:description` / `og:image` / `og:site_name`
 * keys. We translate those at the binding boundary so the rest of the
 * UI sees a strict `snake` → `camel` TypeScript shape.
 *
 * Every field is optional — Synapse returns whatever the origin site
 * exposed. A payload with no title is still a valid preview (the card
 * then renders with just the site name and description), and a payload
 * with only a site name is deliberately suppressed upstream in
 * `useLinkPreview` because it would produce a visually empty card.
 */
export interface LinkPreview {
  /** `og:title` — typically the article / page headline. */
  title?: string;
  /** `og:description` — short summary, one or two sentences. */
  description?: string;
  /** `og:site_name` — publication / site identity (e.g. "nurion.tech").
   *  Falls back to the URL hostname at render time when absent. */
  siteName?: string;
  /** `og:image` mxc URI. The card resolves this to an HTTPS thumbnail
   *  via `mxcToHttp(..., { thumbnail: { w: 160, h: 160, method: 'scale' } })`. */
  imageMxc?: string;
}

export interface PaginateBackwardResult {
  /** Number of new events prepended to the timeline. */
  loaded: number;
  /** True once the server reports no more history. */
  atStart: boolean;
}

/* ── Search ─────────────────────────────────────────────────────── */

/** Dimension a search hit belongs to. Absent on a result means a
 *  message; file hits set `"file"`. */
export type SearchResultKind = "message" | "file";

const FILE_SEARCH_MSGTYPES: ReadonlySet<string> = new Set([
  "m.file",
  "m.image",
  "m.audio",
  "m.video",
]);

export function fileKindFromMsgtype(msgtype?: string | null): SearchResultKind | undefined {
  return msgtype && FILE_SEARCH_MSGTYPES.has(msgtype) ? "file" : undefined;
}

/**
 * One match returned from the Matrix search API. Flat by design — the
 * UI renders a 64px row with the sender, a timestamp, and the body
 * snippet with matched tokens bolded, so `rank` is the only field
 * callers might ever want for sorting and the rest is straight
 * projection off the underlying event.
 *
 * `rank` is the homeserver-supplied relevance score; higher is more
 * relevant. The wrapper does not re-rank — results arrive in the
 * server's order and we preserve that order downstream.
 */
export interface SearchResult {
  roomId: string;
  roomName: string;
  eventId: string;
  sender: string;
  senderDisplayName: string;
  body: string;
  ts: number;
  rank: number;
  kind?: SearchResultKind;
}

/**
 * A single page of search results. `nextBatch` is the opaque cursor the
 * caller must pass back to `searchEvents` to fetch the next page, or
 * `null` when the server has exhausted matches. The shape mirrors the
 * Matrix `/search` response (`next_batch`) so a future real-SDK impl
 * maps one-to-one.
 */
export interface SearchResultsPage {
  results: SearchResult[];
  nextBatch: string | null;
}

/**
 * Minimum options to create a new room.
 */
export interface CreateRoomOptions {
  name?: string;
  isDirect?: boolean;
  isPrivate?: boolean;
  /** Other users to invite. For DMs this is the counterpart. */
  invite?: string[];
  /** Optional topic (channels only). */
  topic?: string;
  /**
   * Initial `com.nurion.channel_type` to stamp at creation. Layered
   * into the room's `initial_state` so the type is visible on the
   * first `/sync` — no flicker between "unset" and the target.
   */
  channelType?: ChannelType;
  /**
   * When set to `"m.space"`, the created room is a Matrix Space.
   * Passed as `creation_content.type` on the `POST /createRoom` request.
   */
  roomType?: string;
  visibleToSpaceId?: string;
}

/**
 * A single user surfaced by homeserver user-directory search. The
 * homeserver returns Matrix user ids and best-effort display names /
 * avatars; we pass through exactly what it gave us. `avatarUrl` is a
 * `mxc://` URI — callers resolve via `mxcToHttp`.
 */
export interface UserDirectoryEntry {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * One match returned from a server-side public-rooms directory search
 * (Matrix `POST /publicRooms`). The shape is a deliberately narrow
 * projection of the spec's `PublicRoomsChunk` — just what the
 * autocomplete row renders. `name` is best-effort: rooms can omit it
 * (the spec calls it `name?`), in which case the homeserver returns
 * the canonical alias or room id.
 *
 */
export interface RoomSearchResult {
  /** Matrix room id (`!localpart:server`). */
  roomId: string;
  /** Display name; falls back to canonical alias / id when unset. */
  name: string;
  /** Canonical alias (`#room:server`) when one is set. */
  canonicalAlias: string | null;
  /** Topic, when the directory entry includes one. */
  topic: string | null;
  /** `mxc://` avatar — null when unset. Caller resolves via `mxcToHttp`. */
  avatarUrl: string | null;
  /** Joined-member count the homeserver reports. */
  memberCount: number;
}

/**
 * One public-room directory page. `nextBatch` is the Matrix
 * `next_batch` cursor projected to camelCase; `undefined` means the
 * homeserver reported no further page.
 */
export interface RoomSearchResultsPage {
  results: RoomSearchResult[];
  nextBatch?: string;
}

/**
 * A single joined-or-invited member of a room. `powerLevel` maps to
 * Matrix's `m.room.power_levels` scale (0 = default, 50 =
 * moderator, 100 = admin). The UI collapses this to role labels.
 */
export interface RoomMember {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  powerLevel: number;
  /** Omitted by older bindings; absent means joined for UI decisions. */
  membership?: "join" | "invite" | "leave" | "ban" | "knock";
}

/**
 * Aggregated reaction summary for a timeline event. Derived from
 * `m.reaction` annotation events on the SDK side; the UI consumes this
 * shape only. `myReactionId` is set when `reactedByMe` is true and
 * points at the caller's `m.reaction` event id so `removeReaction` can
 * redact it.
 */
export interface ReactionSummary {
  emoji: string;
  count: number;
  reactedByMe: boolean;
  myReactionId?: string;
  senderIds?: string[];
}

export const MESSAGE_ACTIONS_KEY = "com.nurion.actions" as const;
export const ACTION_RESPONSE_EVENT_TYPE = "com.nurion.action_response" as const;

export type MessageActionStyle = "primary" | "danger" | "default";

export interface MessageActionButton {
  id: string;
  label: string;
  style: MessageActionStyle;
}

export interface ActionResponse {
  actionId: string;
  sender: { id: string; displayName: string };
  ts: number;
}

const ACTION_ID_PATTERN = /^[a-z0-9_-]{1,64}$/;
const MAX_ACTION_BUTTONS = 5;
const MAX_ACTION_LABEL_CHARS = 40;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMessageActionStyle(value: unknown): value is MessageActionStyle {
  return value === "primary" || value === "danger" || value === "default";
}

function parseActionButton(value: unknown): MessageActionButton | null {
  if (!isPlainRecord(value)) return null;
  const { id, label, style } = value;
  if (typeof id !== "string" || !ACTION_ID_PATTERN.test(id)) return null;
  if (typeof label !== "string") return null;
  const labelChars = [...label].length;
  if (labelChars < 1 || labelChars > MAX_ACTION_LABEL_CHARS) return null;
  if (style === undefined || style === "") return { id, label, style: "default" };
  if (!isMessageActionStyle(style)) return null;
  return { id, label, style };
}

export function parseMessageActions(content: unknown): MessageActionButton[] | null {
  if (!isPlainRecord(content)) return null;
  const actions = content[MESSAGE_ACTIONS_KEY];
  if (!isPlainRecord(actions)) return null;
  const buttons = actions.buttons;
  if (!Array.isArray(buttons) || buttons.length < 1 || buttons.length > MAX_ACTION_BUTTONS) {
    return null;
  }
  const parsed: MessageActionButton[] = [];
  for (const raw of buttons) {
    const button = parseActionButton(raw);
    if (!button || parsed.some((existing) => existing.id === button.id)) return null;
    parsed.push(button);
  }
  return parsed;
}

export function actionResponseTarget(
  content: unknown,
  relatesTo: unknown,
): { targetEventId: string; actionId: string } | null {
  if (!isPlainRecord(content)) return null;
  const inner =
    content.type === "m.state" && isPlainRecord(content.content) ? content.content : content;
  const actionId = inner.action_id;
  const relation = relatesTo ?? inner["m.relates_to"];
  if (typeof actionId !== "string" || actionId.length === 0) return null;
  if (!isPlainRecord(relation)) return null;
  const targetEventId = relation.event_id;
  if (relation.rel_type !== "m.reference" || typeof targetEventId !== "string" || !targetEventId) {
    return null;
  }
  return { targetEventId, actionId };
}

/**
 * Snapshot of a SDK redecryptor report. The `kind` discriminator
 * mirrors the three `RedecryptorReport` variants:
 *
 * - `"resolved"` — per-room report with the affected event ids.
 *   `roomId` is non-empty and `affectedEventIds` is the resolved set.
 * - `"lagging"` — the broadcast stream dropped reports; subscribers
 *   must do a broad refresh because some affected rooms are unknown.
 * - `"backup-available"` — a key backup arrived; per-room
 *   `"resolved"` reports follow for events the SDK can re-decrypt.
 *
 * `sessionId` is reserved for future telemetry; the WASM binding
 * leaves it as the empty string because `ResolvedUtds` does not
 * surface the originating megolm session id.
 */
export type DecryptionReportKind = "resolved" | "lagging" | "backup-available";

export interface DecryptionReport {
  kind: DecryptionReportKind;
  roomId: string;
  sessionId: string;
  affectedEventIds: string[];
}

export type DecryptionReportSubscriber = (report: DecryptionReport) => void;

export interface PusherConfig {
  appId: string;
  pushkey: string;
  gatewayUrl: string;
  appDisplayName: string;
  deviceDisplayName: string;
  defaultPayload?: unknown;
}

export interface NotificationContent {
  senderDisplayName: string;
  roomDisplayName: string;
  body: string;
  isDm: boolean;
  badgeCount: number;
}

/**
 * The minimum SDK surface our wrapper depends on. Replacing the stub with
 * the real WASM bindings means implementing this interface around them.
 */
export type MemberLoadState = "idle" | "loading" | "loaded" | "failed";

export interface SdkBindings {
  homeserverUrl: string;
  hasSession(): Promise<boolean>;
  restoreSession(): Promise<void>;
  /**
   * Build the MAS authorization URL the user must visit to grant
   * access to their Matrix account. Returns `{ url, state }` — the
   * caller opens `url` in the system browser / new tab; `state` is
   * surfaced for diagnostics and tests but the SDK validates it
   * internally on `finishOidcLogin`, so callers do not need to
   * round-trip it themselves. Persisting `state` across tabs would
   * also require persisting the SDK's PKCE verifier (not exposed),
   * so the value is intentionally session-scoped.
   */
  startOidcLogin(redirectUri: string): Promise<{ url: string; state: string }>;
  /**
   * Finish a MAS / OIDC login round-trip. `callbackQuery` is the raw
   * query string of the redirect URI (everything after `?`). The SDK
   * validates `state`, exchanges the authorization `code` for tokens,
   * and activates the session.
   */
  finishOidcLogin(callbackQuery: string): Promise<void>;
  /**
   * Embed entry-point. The host app already minted an access token on
   * the user's behalf and passes it in directly — there is no SSO flow
   * to run. The binding records the access token + user id and stamps
   * the in-memory session as active. The downstream `startSync` /
   * `getDisplayName` / `getUserId` calls then behave the same as a
   * post-SSO session.
   *
   * Real-SDK impls: scope the IndexedDB sync + crypto stores under
   * `accessToken`-derived namespace so two embeds in the same browser
   * can't stomp on each other. The stub is in-memory only; the
   * namespacing is documented but a no-op.
   *
   * `userId` must be the Matrix id the access token belongs to. The
   * binding does not introspect the token — embedders are responsible
   * for handing it the matching pair.
   */
  loginWithAccessToken(userId: string, accessToken: string): Promise<void>;
  startSync(onState: (state: ClientState, error?: unknown) => void): Promise<void>;
  stopSync(): Promise<void>;
  logout(): Promise<void>;
  getDisplayName(): Promise<string | null>;
  /** Read a Matrix profile for any visible user. */
  getUserProfile(userId: string): Promise<UserProfile>;
  requestOpenIdToken(): Promise<OpenIdToken>;
  /** Matrix user id of the signed-in account, or null before login. */
  getUserId(): string | null;
  getDeviceId(): string | null;
  registerPusher(config: PusherConfig): Promise<void>;
  unregisterPusher(appId: string, pushkey: string): Promise<void>;
  resolveNotification(roomId: string, eventId: string): Promise<NotificationContent>;
  /* Crypto — see the Crypto section above for semantics. */
  initCrypto(): Promise<void>;
  bootstrapCrossSigning(interactiveAuth: InteractiveAuthCallback): Promise<void>;
  completeCrossSigningOauth(): Promise<void>;
  cancelCrossSigningOauth(): Promise<void>;
  ensureSecretStorage(): Promise<{ recoveryKey?: string }>;
  ensureKeyBackup(): Promise<void>;
  restoreKeyBackupWithRecoveryKey(recoveryKey: string): Promise<{ restoredCount: number | null }>;
  restoreKeyBackupWithCachedKey(): Promise<{ restoredCount: number | null }>;
  restoreKeyBackupFromVerifiedDevice(): Promise<VerifiedDeviceRestoreResult>;
  hasSecretStorageKey(): Promise<boolean>;
  hasKeyBackup(): Promise<boolean>;
  subscribeVerificationRequests(cb: (req: VerificationRequest) => void): Unsubscribe;
  startVerificationRequest(userId: string, deviceId: string): Promise<VerificationRequest>;
  startSasVerification(userId: string, deviceId: string): Promise<VerificationSession>;
  getOwnDevices(): Promise<MatrixDevice[]>;
  getUserDevices(userId: string): Promise<MatrixDevice[]>;
  getMessageEncryptionStatus(event: MessageEventLike): MessageEncryptionStatus;
  requestKeysForEvent(roomId: string, eventId: string): Promise<void>;
  /**
   * Subscribe to decryption reports from the SDK's event cache
   * redecryptor. Fires whenever previously-UTD events become
   * decryptable — late `m.room_key`, restored backup, or explicit
   * `requestKeysForEvent`. Real-SDK binding drives the underlying
   * `EventCache::subscribe_to_decryption_reports` stream; the stub
   * fan-outs `stubEmitDecryptionReport` events from tests. Returns an
   * unsubscribe function.
   */
  subscribeDecryptionReports(cb: DecryptionReportSubscriber): Unsubscribe;
  isRoomEncrypted(roomId: string): Promise<boolean>;
  /** Read a custom room state event. Returns the parsed content or null.
   *  Real-SDK impls must validate the payload before returning; the stub
   *  is in-process and already typed. */
  getRoomStateEvent<T>(roomId: string, eventType: string, stateKey?: string): Promise<T | null>;
  /** Send (or overwrite) a custom room state event. Requires power level. */
  sendRoomStateEvent<T>(
    roomId: string,
    eventType: string,
    content: T,
    stateKey?: string,
  ): Promise<void>;
  /** Subscribe to one room state event. Fires with the current content first. */
  subscribeRoomStateEvent<T>(
    roomId: string,
    eventType: string,
    stateKey: string | undefined,
    cb: RoomStateSubscriber<T>,
  ): Unsubscribe;
  /**
   * Subscribe to the list of joined rooms. The callback fires once
   * synchronously (best-effort) with the current list and then on every
   * change. Returns an unsubscribe function.
   */
  subscribeRooms(cb: RoomsSubscriber): Unsubscribe;
  subscribeInvitedRooms(cb: InvitedRoomsSubscriber): Unsubscribe;
  subscribeRoomListInitialized(cb: () => void): Unsubscribe;
  /**
   * Subscribe to the mentions activity feed. Real bindings must use
   * homeserver / SDK push actions for mention detection and reaction
   * annotation events whose target event was sent by the local user.
   * Emits newest first and caps at the latest 200 items.
   */
  subscribeMentionsFeed(cb: MentionsFeedSubscriber): Unsubscribe;
  /**
   * Read a global account-data event. Returns the parsed content or
   * null. Used for cross-device user settings (e.g. `com.nurion.saved`).
   * Real-SDK impls must validate before returning; the stub is
   * in-process and already typed.
   */
  getAccountData<T>(eventType: string): Promise<T | null>;
  /**
   * Best-effort read of account-data-like custom state for another user.
   * Real bindings may source this from shared account-data, profile metadata,
   * or presence-compatible fallbacks; callers must tolerate null.
   */
  getUserAccountDataLike<T>(userId: string, eventType: string): Promise<T | null>;
  /**
   * Write (or overwrite) a global account-data event. Syncs across the
   * user's devices via Matrix. Callers must pass a JSON-serialisable
   * content payload — Matrix rejects anything else.
   */
  setAccountData<T>(eventType: string, content: T): Promise<void>;
  /**
   * Subscribe to an account-data event. Fires synchronously with the
   * current snapshot (or null) and then on every change — including
   * remote writes delivered via `/sync`. Returns an unsubscribe function.
   */
  subscribeAccountData<T>(eventType: string, cb: AccountDataSubscriber<T>): Unsubscribe;
  /**
   * Create a room and atomically stamp it with the given initial state
   * events. Maps to the `POST /createRoom` endpoint with `initial_state`;
   * the state event is sent as part of the same request so there is no
   * window where the room exists without it.
   *
   * `preset` maps directly to Matrix spec values: `private_chat`
   * (invite-only channel), `public_chat` (discoverable), or
   * `trusted_private_chat` (DMs — invite-only with the same
   * `users_default` power level across all members so both sides
   * can moderate the conversation together; see the Matrix spec
   * §/createRoom for the precise power-level matrix).
   */
  createRoom(opts: {
    name?: string;
    isDirect: boolean;
    invite: string[];
    topic?: string;
    preset: "private_chat" | "trusted_private_chat";
    initialState: Array<{ type: string; stateKey?: string; content: unknown }>;
    roomType?: string;
    powerLevelContentOverride?: Record<string, unknown>;
  }): Promise<string>;
  /**
   * Update a room's `m.room.name` state event. Requires the caller to
   * have sufficient power level; the SDK surfaces a permission error
   * when they don't and the wrapper re-throws so the UI can toast.
   */
  setRoomName(roomId: string, name: string): Promise<void>;
  /** Update a room's `m.room.topic` state event. */
  setRoomTopic(roomId: string, topic: string): Promise<void>;
  /**
   * Update a room's `m.room.avatar` state event with a `mxc://` URI.
   * Clearing the avatar is out of scope here — pass a non-empty URI.
   */
  setRoomAvatar(roomId: string, mxcUri: string): Promise<void>;
  /** Set the signed-in user's profile photo to a `mxc://` URI, or remove it with `null`. */
  setOwnAvatar(mxcUri: string | null): Promise<void>;
  /**
   * Returns true when `userId` can send `eventType` as a state event
   * in `roomId`. Reads `m.room.power_levels` and compares against the
   * requirement for the event. Synchronous so render-time gating
   * ("show the gear icon?") doesn't need to juggle loading states.
   */
  canSendStateEvent(roomId: string, userId: string, eventType: string): boolean;
  /**
   * Can `userId` notify the whole room (the `@room` / Slack
   * `@channel` affordance)? Synchronous — reads the same
   * `m.room.power_levels` snapshot `canSendStateEvent` uses and
   * compares the user's level against `notifications.room` (default
   * 50). The send path attaches `m.mentions.room` only when this is
   * true; agents are never notified by `@room`.
   */
  canMentionRoom(roomId: string, userId: string): boolean;
  /**
   * Snapshot of the room's membership at call time. Includes joined
   * and invited members; excludes `leave`/`ban` entries. Subscribers
   * for live updates land in a follow-up — for #1310 the member list
   * is re-read each time the panel opens.
   */
  getMembers(roomId: string): RoomMember[];
  getMemberLoadState(roomId: string): MemberLoadState;
  getCachedMember(roomId: string, userId: string): RoomMember | null;
  /**
   * Awaited roster load. Resolves with the room's current members once
   * the transport returns them, rather than peeks the synchronous cache
   * `getMembers` does — a DM's org eligibility must never decide off an
   * un-loaded cache. Shared by the org roster builder; keep the cache
   * refresh behaviour so a later `getMembers` peek is warm.
   */
  loadMembers(roomId: string): Promise<RoomMember[]>;
  /**
   * Invite `userId` to `roomId`. The homeserver validates power
   * level and membership rules; the wrapper re-throws so the dialog
   * can render the failure inline.
   */
  inviteUser(roomId: string, userId: string): Promise<void>;
  /** Remove `userId` from `roomId` with an optional reason. */
  kickUser(roomId: string, userId: string, reason?: string): Promise<void>;
  /** Ban `userId` from `roomId` with an optional reason. */
  banUser(roomId: string, userId: string, reason?: string): Promise<void>;
  /** Leave `roomId` as the current user. Maps to POST /rooms/{roomId}/leave. */
  leaveRoom(roomId: string): Promise<void>;
  acceptInvite(roomId: string): Promise<void>;
  declineInvite(roomId: string): Promise<void>;
  /** Forget a left room so it does not reappear in local room lists on next sync. */
  forgetRoom(roomId: string): Promise<void>;
  /** Set a member's room power level. Used to hand off admin before leaving. */
  setMemberPowerLevel(roomId: string, userId: string, powerLevel: number): Promise<void>;
  /**
   * Homeserver user-directory search. `term` is a substring match
   * against display names / Matrix ids. Returns up to 10 entries by
   * convention — matches what Synapse caps by default.
   */
  searchUserDirectory(term: string): Promise<UserDirectoryEntry[]>;
  /**
   * Server-side public-rooms directory search. Maps to Matrix's
   * `POST /_matrix/client/v3/publicRooms` with a `generic_search_term`
   * filter plus optional `limit` and `since` cursor. `term` is a
   * free-text substring against name / topic / canonical alias. Returns
   * one raw homeserver page; the wrapper paginates. The returned page
   * is the homeserver's choice — we do not re-rank.
   */
  searchRooms(
    term: string,
    opts?: { limit?: number; cursor?: string },
  ): Promise<RoomSearchResultsPage>;
  /**
   * Send an `m.reaction` annotation of `emoji` against `targetEventId`.
   * Returns the reaction event id. The caller is expected to react only
   * when the user hasn't already reacted with the same emoji — the UI
   * layer decides toggling semantics through `removeReaction`.
   */
  sendReaction(roomId: string, targetEventId: string, emoji: string): Promise<string>;
  /** Redact a previously sent `m.reaction` event. */
  removeReaction(roomId: string, reactionEventId: string): Promise<void>;
  /**
   * Send an `m.replace` edit of `targetEventId` with `newBody`. Returns
   * the new event id. Real-SDK impls emit both the replacement body and
   * `m.new_content`; the UI layer observes `editedAt` through the
   * timeline subscription.
   */
  editText(roomId: string, targetEventId: string, newBody: string): Promise<string>;
  /** Redact an event with an optional human-readable reason. */
  redactEvent(roomId: string, eventId: string, reason?: string): Promise<void>;
  /**
   * Fetch one room event by id from the homeserver. Maps to Matrix
   * `GET /_matrix/client/v3/rooms/{roomId}/event/{eventId}`. Returns
   * `null` when the event is unavailable to the current user.
   */
  getRoomEvent(roomId: string, eventId: string): Promise<TimelineEvent | null>;
  /**
   * Subscribe to a room's timeline. When an in-memory buffer exists, the
   * callback fires synchronously with it; otherwise the binding may first
   * emit a local cache snapshot before the authoritative server refresh
   * lands. Subsequent changes include new events, local-echo state
   * transitions, redactions, and pagination.
   */
  subscribeTimeline(
    roomId: string,
    cb: TimelineSubscriber,
    opts?: TimelineSubscriptionOptions,
  ): Unsubscribe;
  /**
   * Request `limit` older events from the server. The SDK prepends them
   * to the in-memory buffer and notifies timeline subscribers. Returns
   * `{ loaded, atStart }` so the UI can collapse the loading spinner.
   */
  paginateBackward(roomId: string, limit: number): Promise<PaginateBackwardResult>;
  /** Load an event-focused window for a jumped-to event into the buffer before the UI scrolls onto it. */
  jumpToEvent(
    roomId: string,
    eventId: string,
    opts?: { limit?: number },
  ): Promise<{ found: boolean }>;
  /**
   * Fetch one earlier page of a thread's replies using the per-thread
   * cursor the binding owns. The initial `subscribeThread` loads only
   * the newest page; each call fetches exactly one more cursor page and
   * merges it into the retained window without refetching the newest
   * page. Reuses the room backward contract (`{ loaded, atStart }`);
   * the cursor itself stays internal to the binding.
   */
  paginateThreadBackward(
    roomId: string,
    rootEventId: string,
    limit: number,
  ): Promise<PaginateBackwardResult>;
  /**
   * Send a plain-text message. Returns the event id. Local-echo state
   * transitions are visible through `subscribeTimeline` — `sending`
   * appears immediately, flips to `sent` (or `failed`) asynchronously.
   *
   * `formattedBody` carries Matrix-spec HTML for the `formatted_body`
   * field, paired with `format: "org.matrix.custom.html"`. Used by the
   * composer's mention serialisation to ship pill-rendered links
   * (`<a href="https://matrix.to/#/@user:server">…</a>`) while keeping
   * `body` as the plain-text fallback for clients that ignore HTML.
   * Omit for plain text — the binding must not emit a formatted body
   * field when it's unset, so non-formatting senders stay byte-clean.
   *
   * `mentions` carries the mentioned user MXIDs for the spec
   * `m.mentions.user_ids` intentional-mentions field. Omit (or pass
   * empty) for sends with no user mentions — the binding must not emit
   * an `m.mentions` key in that case.
   */
  sendText(
    roomId: string,
    body: string,
    formattedBody?: string,
    txnId?: string,
    mentions?: string[],
    mentionRoom?: boolean,
  ): Promise<string>;
  /** Send a Matrix-standard emote message (`m.emote`). */
  sendEmote(roomId: string, body: string): Promise<string>;
  /**
   * Send a raw `m.room.message` content (the caller already validated
   * `msgtype` / `body`) through the SDK, showing a local `m.text` echo
   * reconciled to the confirmed server event id. Used by agent slash
   * commands so an agent receives `com.nurion.command: { name, args }`
   * even in mixed rooms. Encrypts automatically in encrypted rooms.
   */
  sendRawMessage(roomId: string, content: Record<string, unknown>, txnId?: string): Promise<string>;
  sendRawEvent(
    roomId: string,
    eventType: string,
    content: Record<string, unknown>,
    txnId?: string,
  ): Promise<string>;
  /**
   * Re-drive a failed local echo in place. `eventId` is the failed
   * echo's local id (`subscribeTimeline` surfaced it with
   * `status: "failed"`). The binding flips the existing echo
   * `failed -> sending`, re-sends its body + mentions reusing the
   * original transaction id (so the homeserver dedupes if the prior
   * attempt actually landed), and resolves with the confirmed event
   * id on success — without minting a second echo. On failure the
   * echo flips back to `failed`. Throws when no failed echo with
   * that id exists in the room.
   */
  resendLocalEcho(roomId: string, eventId: string): Promise<string>;
  /**
   * Mark `eventId` as the latest read message in `roomId`. Matrix
   * semantics: the server clears the unread count for everything up to
   * and including this event.
   */
  sendReadReceipt(roomId: string, eventId: string): Promise<void>;
  /** Report an event to the homeserver admins (Matrix `report` endpoint). */
  reportEvent(roomId: string, eventId: string, reason: string): Promise<void>;
  /**
   * Subscribe to `m.fully_read` marker changes for `roomId`. Fires once
   * synchronously with the current marker, then on every `/sync` update.
   */
  subscribeReadMarker(roomId: string, cb: ReadMarkerSubscriber): Unsubscribe;
  /**
   * Call Synapse's `/_matrix/media/v3/preview_url?url=…&ts=…`. Returns
   * the translated OpenGraph payload or `null` on any network / server
   * / shape error. Matrix has no concept of "preview unavailable" — the
   * homeserver either returns a 200 with possibly-empty `og:*` keys,
   * or an error status. Either way the UI renders nothing; the
   * binding collapses both into `null`.
   *
   * `ts` is the event's `origin_server_ts`. Synapse uses it to key the
   * server-side preview cache so two events for the same URL moments
   * apart share a cached fetch. The spec marks it optional — omit it
   * for interactive uses (search composer, admin panel).
   */
  getUrlPreview(url: string, ts?: number): Promise<LinkPreview | null>;
  /**
   * Upload a blob to the Matrix media repository. Returns the `mxc://`
   * URI the server assigned. `onProgress` fires with a percentage in
   * `[0, 100]` as the upload makes progress — the stub synthesises a
   * handful of steps so UI stories can render every branch. Real-SDK
   * impls back this with a Rust `UploadHandle` and abort via `signal`.
   */
  uploadContent(
    blob: Blob,
    opts?: { onProgress?: (pct: number) => void; signal?: AbortSignal },
  ): Promise<{ mxc: string }>;
  /**
   * Removed in #3147 — production callers must use `downloadMedia`
   * via `useAuthenticatedMedia`. The binding entry remains on the
   * interface so the parity audit can pin the post-removal
   * contract: real implementations (Rust binding) throw a "use
   * useAuthenticatedMedia" error; the stub keeps a working
   * implementation because Storybook fixtures never round-trip
   * through Synapse and need a synchronous href.
   *
   * Pre-v1.11 the wrapper handed back a v3 unauthenticated URL the
   * browser could load directly. Synapse ≥ 1.120 ships with
   * `enable_authenticated_media: true` and rejects that URL — the
   * real fetch must go through `downloadMedia` so the SDK attaches
   * the `Authorization: Bearer` header.
   */
  mxcToHttp(
    mxcUri: string,
    opts?: { thumbnail?: { w: number; h: number; method: "scale" | "crop" } },
  ): string;
  /** Download Matrix media bytes through the authenticated SDK path.
   *  Encrypted sources are decrypted before bytes reach the browser. */
  downloadMedia(
    source: MatrixMediaSource,
    opts?: { thumbnail?: { w: number; h: number; method: "scale" | "crop" } },
  ): Promise<MatrixMediaBytes>;
  /**
   * Subscribe to the live list of thread replies for a given root event.
   * The callback fires once synchronously with the current ordered list
   * (chronological, oldest first) and on every subsequent change. The
   * real SDK yields a paginating timeline; the wrapper shape matches the
   * subset we render.
   */
  subscribeThread(roomId: string, rootEventId: string, cb: ThreadSubscriber): Unsubscribe;
  listThreadRoots(roomId: string, from?: string, limit?: number): Promise<ThreadRootsPage>;
  /** Thread-scoped read marker. Real SDK maps this to threaded receipts. */
  getThreadReadReceipt(roomId: string, rootEventId: string): string | null;
  sendThreadReadReceipt(roomId: string, rootEventId: string, eventId: string): Promise<void>;
  /**
   * Send a thread reply. Returns the new event id. `latestReplyId` is
   * the most recent event id in the thread at the time of send — used
   * for the `m.in_reply_to` fallback reference. When the thread has no
   * replies yet, callers pass the root event id.
   */
  sendThreadReply(
    roomId: string,
    rootEventId: string,
    body: string,
    latestReplyId: string,
    options?: ThreadReplyOptions,
  ): Promise<string>;
  /** Send an `m.image` message. */
  sendImage(roomId: string, msg: SendImageMessage): Promise<string>;
  /** Send an `m.video` message. */
  sendVideo(roomId: string, msg: SendVideoMessage): Promise<string>;
  /** Send an `m.file` message. */
  sendFile(roomId: string, msg: SendBinaryMediaMessage): Promise<string>;
  /**
   * Subscribe to presence updates for a single user. Callback fires
   * once synchronously with the current snapshot (best-effort — real
   * SDK may emit a slight delay while the initial `/sync` resolves)
   * and then on every state change. Returns an unsubscribe function.
   */
  subscribePresence(userId: string, cb: PresenceSubscriber): Unsubscribe;
  /** Snapshot a user's current presence without subscribing. */
  getUserPresence(userId: string): PresenceSnapshot;
  /** Set the local user's presence state. */
  setPresence(state: PresenceState): Promise<void>;
  /** Set the local user's Matrix `status_msg` without changing presence state. */
  setPresenceStatus(statusMsg: string | null): Promise<void>;
  /**
   * Subscribe to typing events for a room. Callback receives the
   * list of user ids currently typing (excluding the local user).
   * Fires once synchronously with the current list and then on every
   * change.
   */
  subscribeTyping(roomId: string, cb: TypingSubscriber): Unsubscribe;
  /**
   * Broadcast the local user's typing state. `timeoutMs` is the
   * server-side expiration — the Matrix contract automatically clears
   * the state after this window without a refresh, so clients
   * retransmit periodically while the user still types. Callers
   * (MessageComposer) are responsible for the debounce cadence; this
   * is the transport.
   */
  setTyping(roomId: string, typing: boolean, timeoutMs?: number): Promise<void>;
  /**
   * Return the receipts attached to `eventId` in `roomId`. Real SDK
   * reads the `m.receipt` state the homeserver aggregates from peer
   * devices. Does not include the local user's own receipt — the
   * timeline renders receipts for *other* readers. `threadRootEventId`
   * selects that thread's receipt scope; omit it for the room timeline.
   */
  getReceiptsForEvent(roomId: string, eventId: string, threadRootEventId?: string): Receipt[];
  /**
   * Run a full-text search against the homeserver. Maps to
   * `POST /_matrix/client/v3/search` with the `room_events` category.
   * When `roomId` is set, the search is scoped to that room via the
   * spec's `filter.rooms` constraint; otherwise the server walks every
   * room the user is joined to. `nextBatch` continues a prior query;
   * omit it for the first page.
   *
   * Returns `{ results, nextBatch }`. Empty `results` is a valid
   * answer (a genuine no-match); a `null` `nextBatch` signals the
   * server has no more pages.
   */
  searchEvents(query: string, roomId?: string, nextBatch?: string): Promise<SearchResultsPage>;
  /**
   * Subscribe to receipt-update notifications for a room. The
   * callback carries no payload — it's a coarse "receipts changed;
   * re-read via `getReceiptsForEvent`" signal. Used by consumers
   * that render receipt stacks inline in a timeline: every update
   * triggers a re-render, which re-reads the receipt snapshot for
   * every rendered event. Returns an unsubscribe function.
   */
  subscribeReceipts(roomId: string, cb: ReceiptsSubscriber): Unsubscribe;
  /**
   * Resolve the push actions the homeserver evaluated for `eventId`.
   * Returns `null` when the event isn't known to the client or
   * push-rule evaluation hasn't completed yet. Real SDK reads the
   * event's `getPushActions()` / equivalent; the wrapper collapses
   * "no decision yet" and "unknown event" into a single `null` so
   * callers can skip without a second branch.
   */
  getPushActionsForEvent(eventId: string): PushActions | null;
  /**
   * Read the UI-level per-room notification level from push rules.
   * The mapping is:
   *
   *   - explicit `notify` override    → "all"
   *   - explicit `dont_notify` override → "mute"
   *   - anything else                  → "mentions" (default)
   */
  getRoomNotificationLevel(roomId: string): RoomNotificationLevel;
  /**
   * Persist a per-room notification level to the homeserver as a
   * `room` push rule override. `mentions` removes any existing
   * override (so global rules govern mentions). Resolves once the
   * rule change round-trips.
   */
  setRoomNotificationLevel(roomId: string, level: RoomNotificationLevel): Promise<void>;
  /**
   * Subscribe to aggregate unread / highlight counts across every
   * joined room. Fires synchronously with the current snapshot and
   * on every change (new message, read receipt, room join/leave,
   * per-room mute flip).
   */
  subscribeTotalUnread(cb: UnreadCountsSubscriber): Unsubscribe;
}

export interface Client {
  readonly homeserverUrl: string;
  readonly bindings: SdkBindings;
}

/**
 * Build a Client bound to a homeserver. The actual Matrix session is loaded
 * lazily via `tryRestoreSession`.
 */
export async function createClient(homeserverUrl: string): Promise<Client> {
  const bindings = await loadBindings(homeserverUrl);
  return { homeserverUrl, bindings };
}

/**
 * Build a Client pre-authed with a Matrix access token the host app
 * minted (the embed entry-point). No SSO flow runs — the caller has
 * already exchanged credentials for a token via its own auth surface.
 *
 * `userId` must match the access token. The binding does not
 * introspect the token; passing a mismatched pair will surface as a
 * server-side `M_UNKNOWN_TOKEN` on the first sync call.
 *
 * The function does not start sync — callers must invoke `startSync`
 * once they have wired their `onState` callback. This matches the
 * post-SSO flow so `MatrixProvider` can take either path through the
 * same downstream pipeline.
 */
export async function createClientFromToken(
  homeserverUrl: string,
  userId: string,
  accessToken: string,
): Promise<Client> {
  if (typeof homeserverUrl !== "string" || homeserverUrl.length === 0) {
    throw new Error("createClientFromToken: homeserverUrl must be a non-empty string");
  }
  if (typeof userId !== "string" || !userId.startsWith("@")) {
    throw new Error("createClientFromToken: userId must be a Matrix user id (e.g. @alice:server)");
  }
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    throw new Error("createClientFromToken: accessToken must be a non-empty string");
  }
  const bindings = await loadBindings(homeserverUrl);
  await bindings.loginWithAccessToken(userId, accessToken);
  return { homeserverUrl, bindings };
}

/** Returns true if a session was restored from local storage. */
export async function tryRestoreSession(client: Client): Promise<boolean> {
  if (!(await client.bindings.hasSession())) return false;
  try {
    await client.bindings.restoreSession();
    return true;
  } catch (err) {
    if (isStaleCryptoStoreError(err)) throw err;
    return false;
  }
}

/**
 * Build the MAS authorization URL the user must visit to grant access
 * to their Matrix account. Returns `{ url, state }` — the caller is
 * responsible for opening the URL (the binding stays pure and does
 * not touch the platform layer; aligns with the rest of the binding
 * surface). `state` is surfaced for diagnostics and tests; the SDK
 * validates it internally on `finishOidcLogin`, and the SDK's PKCE
 * verifier is held in the live wasm instance (not exposed for
 * persistence), so the OAuth round-trip is implicitly bound to a
 * single client lifetime. A tab close mid-flow forces a re-login on
 * the next visit.
 *
 * Throws a descriptive error when MAS metadata discovery fails (e.g.
 * the homeserver is not MAS-fronted) — see ticket Anti-patterns.
 */
export async function startOidcLogin(
  client: Client,
  redirectUrl: string,
): Promise<{ url: string; state: string }> {
  return client.bindings.startOidcLogin(redirectUrl);
}

/**
 * Finish a MAS / OIDC login round-trip. `callbackQuery` is the raw
 * query string of the redirect URI (everything after the leading `?`).
 * The SDK validates `state`, exchanges the authorization `code` for
 * tokens, and activates the session.
 */
export async function finishOidcLogin(client: Client, callbackQuery: string): Promise<void> {
  await client.bindings.finishOidcLogin(callbackQuery);
}

export async function startSync(
  client: Client,
  onState: (state: ClientState, error?: unknown) => void,
): Promise<void> {
  await client.bindings.startSync(onState);
}

export async function stopSync(client: Client): Promise<void> {
  await client.bindings.stopSync();
}

export async function logout(client: Client, opts: { broadcast?: boolean } = {}): Promise<void> {
  const storeName = storeNameFromSession(await readSecret("matrix.session").catch(() => null));
  let logoutError: unknown = null;
  try {
    await client.bindings.logout();
  } catch (err) {
    logoutError = err;
    console.warn("Matrix logout failed before local cleanup", err);
  } finally {
    await deleteChatIndexedDbDatabases(MATRIX_SESSION_STORE_PREFIX, storeName);
    await clearAppIconBadge();
    if (opts.broadcast !== false) notifySignOutBroadcast();
  }
  if (logoutError) throw logoutError;
}

async function clearAppIconBadge(): Promise<void> {
  if (platform !== "tauri") return;
  try {
    const { setBadgeCount } = (await import("@tauri-apps/api/app")) as {
      setBadgeCount?: (count?: number) => Promise<void>;
    };
    await setBadgeCount?.(undefined);
  } catch (err) {
    console.warn("Matrix logout badge clear failed", err);
  }
}

/**
 * Whether `err` is a Matrix `M_UNKNOWN_TOKEN` (or equivalent) reported by
 * matrix-rust-sdk's wasm boundary. The SDK does not surface a typed
 * error variant through wasm, so we match on the formatted message
 * matrix-rust-sdk produces — `the server returned an error: [401 /
 * M_UNKNOWN_TOKEN] Token is not active` is the format observed against
 * MAS at our pinned rev.
 *
 * Important: matrix-rust-sdk's wasm-bindgen boundary throws values as
 * **plain strings**, not as `Error` instances. (Confirmed empirically
 * — `err instanceof Error` is `false` for the boot-time failures.)
 * We accept any input that exposes a string-coercible payload: an
 * `Error`, a plain string, or any object with a `.message` property.
 * Anything else returns `false` so we don't dump a session on a
 * non-token failure shape.
 *
 * Distinguishing this from generic 401s matters because the recovery is
 * specific: wipe the persisted session blob and IndexedDB store, fall
 * back to the login screen so the user can sign in fresh. Other 401s
 * (e.g. transient gateway failures) should keep the existing error
 * surface so we don't silently dump a session on a recoverable blip.
 */
export function isUnknownTokenError(err: unknown): boolean {
  const msg = matrixErrorMessage(err);
  if (!msg) return false;
  return msg.includes("M_UNKNOWN_TOKEN") || /Token is not active/i.test(msg);
}

export function isStaleCryptoStoreError(err: unknown): boolean {
  return matrixErrorMessage(err)?.includes("account in the store") ?? false;
}

/** The SDK reports a revoked/expired OAuth grant as an `invalid_grant` token-refresh failure, which no refresh can recover (unlike `M_UNKNOWN_TOKEN`). */
export function isInvalidGrantError(err: unknown): boolean {
  return /invalid_grant/i.test(matrixErrorMessage(err) ?? "");
}

export class BackupExistsError extends Error {
  readonly kind = "backup-exists";

  constructor() {
    super("backup exists on server");
    this.name = "BackupExistsError";
  }
}

export function isBackupExistsError(err: unknown): err is BackupExistsError {
  return err instanceof BackupExistsError || (err as { kind?: unknown })?.kind === "backup-exists";
}

function matrixErrorMessage(err: unknown): string | null {
  if (typeof err === "string") {
    return err;
  }
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object") {
    const candidate = (err as { message?: unknown }).message;
    if (typeof candidate === "string") return candidate;
  }
  return null;
}

// Reads the per-session matrix-rust-sdk store name from the persisted
// session blob written by `matrix-sdk-rust.ts` (see `parseStoredSession`
// and the OIDC / embed paths, which serialise
// `{ storeName, sessionJson, kind }`). Before #3187 the reader looked
// up a stale README-vocabulary field name while every writer persisted
// `storeName`, so the helper silently returned `undefined` and the
// logout cleanup became a no-op — orphan matrix-rust-sdk IDB databases
// piled up across sign-in / sign-out cycles. The function and the
// persisted field are now spelled the same on both sides.
export function storeNameFromSession(raw: string | null): string | null {
  if (!raw || raw === "1") return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const storeName = (parsed as { storeName?: unknown }).storeName;
    return typeof storeName === "string" && storeName.length > 0 ? storeName : null;
  } catch {
    return null;
  }
}

export async function getDisplayName(client: Client): Promise<string | null> {
  return client.bindings.getDisplayName();
}

/** Read a Matrix profile for any user visible to the current account. */
export async function getUserProfile(client: Client, userId: string): Promise<UserProfile> {
  if (!userId.startsWith("@")) {
    throw new Error("getUserProfile: userId must be a Matrix user id (e.g. @alice:server)");
  }
  return client.bindings.getUserProfile(userId);
}

export function requestOpenIdToken(client: Client): Promise<OpenIdToken> {
  return client.bindings.requestOpenIdToken();
}

/**
 * Matrix user id of the signed-in account, or `null` before login.
 * Callers doing equality checks ("is this my message?") should compare
 * against this explicitly; `null === anything` is always false, which
 * is exactly the right behaviour for pre-login rows.
 */
export function getUserId(client: Client): string | null {
  return client.bindings.getUserId();
}

/* ── Reactions, edits, redactions ─────────────────────────────────── */

/**
 * React to `targetEventId` with `emoji`. Matrix-standard annotation
 * relation (`m.annotation`); the homeserver aggregates counts on the
 * timeline event, which the UI reads via `timelineEvent.reactions`.
 *
 * Returns the reaction event id; callers who want to toggle later pass
 * this through `removeReaction`. Empty emoji is rejected at the wrapper
 * so a malformed round-trip can't produce a phantom "" reaction in the
 * aggregation bucket.
 */
export async function sendReaction(
  client: Client,
  roomId: string,
  targetEventId: string,
  emoji: string,
): Promise<string> {
  if (typeof emoji !== "string" || emoji.length === 0) {
    throw new Error("sendReaction: emoji must be a non-empty string");
  }
  return client.bindings.sendReaction(roomId, targetEventId, emoji);
}

/**
 * Redact a reaction the current user sent. The caller passes the
 * `myReactionId` from the `ReactionSummary` — that's how an "active"
 * chip click finds the correct event to redact.
 */
export async function removeReaction(
  client: Client,
  roomId: string,
  reactionEventId: string,
): Promise<void> {
  await client.bindings.removeReaction(roomId, reactionEventId);
}

/**
 * Edit a text message. The new body becomes the displayed content; an
 * `editedAt` timestamp appears on the timeline event so the UI can
 * render "(edited)". Returns the replacement event id.
 *
 * Empty `newBody` is rejected: edit-to-empty is a delete, not an edit,
 * and should go through `redactEvent` instead so the intent is explicit
 * in the Matrix audit log.
 */
export async function editText(
  client: Client,
  roomId: string,
  targetEventId: string,
  newBody: string,
): Promise<string> {
  if (typeof newBody !== "string" || newBody.trim().length === 0) {
    throw new Error("editText: newBody must not be empty");
  }
  return client.bindings.editText(roomId, targetEventId, newBody);
}

/**
 * Redact (delete) an event. `reason` is optional and surfaces in the
 * Matrix audit trail; the UI default is "deleted by user". Redacted
 * events render as "[message deleted]".
 */
export async function redactEvent(
  client: Client,
  roomId: string,
  eventId: string,
  reason?: string,
): Promise<void> {
  await client.bindings.redactEvent(roomId, eventId, reason);
}

/* ── Pinned events ───────────────────────────────────────────────── */

function normalisePinnedEvents(raw: unknown): string[] {
  if (!raw || typeof raw !== "object") return [];
  const pinned = (raw as { pinned?: unknown }).pinned;
  if (!Array.isArray(pinned)) return [];
  const out: string[] = [];
  for (const eventId of pinned) {
    if (typeof eventId !== "string" || eventId.length === 0) continue;
    if (out.includes(eventId)) continue;
    out.push(eventId);
  }
  return out;
}

const pinnedMutationQueues = new WeakMap<SdkBindings, Map<string, Promise<void>>>();

// Serialises read-modify-write operations issued by this client so two
// local pin actions do not overwrite each other. Matrix state events do
// not provide a portable compare-and-swap primitive at this wrapper seam,
// so competing writes from other devices still follow Matrix's normal
// last-writer state semantics.
async function withPinnedMutation<T>(
  client: Client,
  roomId: string,
  mutation: () => Promise<T>,
): Promise<T> {
  let roomQueues = pinnedMutationQueues.get(client.bindings);
  if (!roomQueues) {
    roomQueues = new Map();
    pinnedMutationQueues.set(client.bindings, roomQueues);
  }
  const previous = roomQueues.get(roomId) ?? Promise.resolve();
  let release: () => void = () => {};
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  const queued = previous.then(
    () => current,
    () => current,
  );
  roomQueues.set(roomId, queued);
  await previous.catch(() => undefined);
  try {
    return await mutation();
  } finally {
    release();
    if (roomQueues.get(roomId) === queued) roomQueues.delete(roomId);
  }
}

export async function getPinned(client: Client, roomId: string): Promise<string[]> {
  const raw = await client.bindings.getRoomStateEvent<unknown>(roomId, PINNED_EVENTS_TYPE);
  return normalisePinnedEvents(raw);
}

export function subscribePinned(
  client: Client,
  roomId: string,
  cb: (eventIds: string[]) => void,
): Unsubscribe {
  return client.bindings.subscribeRoomStateEvent<unknown>(
    roomId,
    PINNED_EVENTS_TYPE,
    undefined,
    (raw) => cb(normalisePinnedEvents(raw)),
  );
}

export async function pinEvent(client: Client, roomId: string, eventId: string): Promise<string[]> {
  if (typeof eventId !== "string" || eventId.length === 0) {
    throw new Error("pinEvent: eventId must be a non-empty string");
  }
  return withPinnedMutation(client, roomId, async () => {
    const current = await getPinned(client, roomId);
    const next = [eventId, ...current.filter((id) => id !== eventId)];
    await client.bindings.sendRoomStateEvent<PinnedEventsStateContent>(roomId, PINNED_EVENTS_TYPE, {
      pinned: next,
    });
    return next;
  });
}

export async function unpinEvent(
  client: Client,
  roomId: string,
  eventId: string,
): Promise<string[]> {
  if (typeof eventId !== "string" || eventId.length === 0) {
    throw new Error("unpinEvent: eventId must be a non-empty string");
  }
  return withPinnedMutation(client, roomId, async () => {
    const current = await getPinned(client, roomId);
    const next = current.filter((id) => id !== eventId);
    if (next.length === current.length) return current;
    await client.bindings.sendRoomStateEvent<PinnedEventsStateContent>(roomId, PINNED_EVENTS_TYPE, {
      pinned: next,
    });
    return next;
  });
}

export async function clearPinned(client: Client, roomId: string): Promise<void> {
  await withPinnedMutation(client, roomId, async () => {
    await client.bindings.sendRoomStateEvent<PinnedEventsStateContent>(roomId, PINNED_EVENTS_TYPE, {
      pinned: [],
    });
  });
}

/**
 * Subscribe to the live list of joined rooms. The subscriber is invoked
 * with the current list once (synchronously where the SDK allows) and
 * then on every change.
 */
export function subscribeRooms(client: Client, cb: RoomsSubscriber): Unsubscribe {
  return client.bindings.subscribeRooms(cb);
}

export function subscribeInvitedRooms(client: Client, cb: InvitedRoomsSubscriber): Unsubscribe {
  return client.bindings.subscribeInvitedRooms(cb);
}

export function normaliseArchived(raw: unknown): ArchivedStateContent | null {
  if (!raw || typeof raw !== "object") return null;
  const input = raw as {
    archivedAt?: unknown;
    archivedBy?: unknown;
    reason?: unknown;
    previousEventsDefault?: unknown;
    previousMessageEventLevel?: unknown;
  };
  if (typeof input.archivedAt !== "number" || !Number.isFinite(input.archivedAt)) return null;
  if (input.archivedAt <= 0) return null;
  if (typeof input.archivedBy !== "string" || input.archivedBy.length === 0) return null;
  return {
    archivedAt: input.archivedAt,
    archivedBy: input.archivedBy,
    ...(typeof input.reason === "string" && input.reason.length > 0
      ? { reason: input.reason }
      : {}),
    ...(typeof input.previousEventsDefault === "number" &&
    Number.isFinite(input.previousEventsDefault)
      ? { previousEventsDefault: input.previousEventsDefault }
      : {}),
    ...(typeof input.previousMessageEventLevel === "number" &&
    Number.isFinite(input.previousMessageEventLevel)
      ? { previousMessageEventLevel: input.previousMessageEventLevel }
      : input.previousMessageEventLevel === null
        ? { previousMessageEventLevel: null }
        : {}),
  };
}

function normalisePowerLevels(raw: unknown): PowerLevelsStateContent {
  if (!raw || typeof raw !== "object") return {};
  const input = raw as PowerLevelsStateContent;
  return { ...input };
}

async function getRoomPowerLevels(
  client: Client,
  roomId: string,
): Promise<PowerLevelsStateContent> {
  return normalisePowerLevels(
    await client.bindings.getRoomStateEvent<unknown>(roomId, POWER_LEVELS_EVENT_TYPE),
  );
}

function numericPowerLevel(value: number | string | undefined): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

async function setRoomPowerLevels(
  client: Client,
  roomId: string,
  content: PowerLevelsStateContent,
): Promise<void> {
  await client.bindings.sendRoomStateEvent<PowerLevelsStateContent>(
    roomId,
    POWER_LEVELS_EVENT_TYPE,
    content,
  );
}

function archivedPowerLevels(current: PowerLevelsStateContent): PowerLevelsStateContent {
  return {
    ...current,
    events_default: 100,
    events: { ...(current.events ?? {}), "m.room.message": 100 },
  };
}

function restoredPowerLevels(
  current: PowerLevelsStateContent,
  archived: ArchivedStateContent | null,
): PowerLevelsStateContent {
  const next: PowerLevelsStateContent = { ...current };
  if (archived?.previousEventsDefault === undefined) {
    delete next.events_default;
  } else {
    next.events_default = archived.previousEventsDefault;
  }
  const events = { ...(current.events ?? {}) };
  if (
    archived?.previousMessageEventLevel === undefined ||
    archived.previousMessageEventLevel === null
  ) {
    delete events["m.room.message"];
  } else {
    events["m.room.message"] = archived.previousMessageEventLevel;
  }
  if (Object.keys(events).length === 0) {
    delete next.events;
  } else {
    next.events = events;
  }
  return next;
}

export async function archiveRoom(
  client: Client,
  roomId: string,
  archivedBy: string,
  reason?: string,
): Promise<void> {
  if (!archivedBy.startsWith("@")) {
    throw new Error("archiveRoom: archivedBy must be a Matrix user id");
  }
  const currentPowerLevels = await getRoomPowerLevels(client, roomId);
  await client.bindings.sendRoomStateEvent<ArchivedStateContent>(roomId, ARCHIVED_EVENT_TYPE, {
    archivedAt: Date.now(),
    archivedBy,
    previousEventsDefault: numericPowerLevel(currentPowerLevels.events_default) ?? 0,
    previousMessageEventLevel:
      numericPowerLevel(currentPowerLevels.events?.["m.room.message"]) ?? null,
    ...(reason ? { reason } : {}),
  });
  try {
    await setRoomPowerLevels(client, roomId, archivedPowerLevels(currentPowerLevels));
  } catch (error) {
    await client.bindings.sendRoomStateEvent<ArchivedStateContent>(roomId, ARCHIVED_EVENT_TYPE, {
      archivedAt: 0,
      archivedBy,
      reason: "archive rollback",
    });
    throw error;
  }
}

export async function unarchiveRoom(
  client: Client,
  roomId: string,
  archivedBy: string,
): Promise<void> {
  if (!archivedBy.startsWith("@")) {
    throw new Error("unarchiveRoom: archivedBy must be a Matrix user id");
  }
  const archived = normaliseArchived(
    await client.bindings.getRoomStateEvent<unknown>(roomId, ARCHIVED_EVENT_TYPE),
  );
  await setRoomPowerLevels(
    client,
    roomId,
    restoredPowerLevels(await getRoomPowerLevels(client, roomId), archived),
  );
  await client.bindings.sendRoomStateEvent<ArchivedStateContent>(roomId, ARCHIVED_EVENT_TYPE, {
    archivedAt: 0,
    archivedBy,
    reason: "unarchived",
  });
}

/**
 * Subscribe to mention and reaction activity.
 * Detection belongs at the SDK seam: mentions come from push actions,
 * reactions come from relation events targeting the local user's events.
 */
export function subscribeMentionsFeed(client: Client, cb: MentionsFeedSubscriber): Unsubscribe {
  return client.bindings.subscribeMentionsFeed(cb);
}

export const CHANNEL_EVENT_POWER_LEVELS: Readonly<Record<string, number>> = {
  "m.room.name": 50,
  "m.room.power_levels": 100,
  "m.room.history_visibility": 100,
  "m.room.canonical_alias": 50,
  "m.room.avatar": 50,
  "m.room.tombstone": 150,
  "m.room.server_acl": 100,
  "m.room.encryption": 100,
  [MEETING_ACTIVE_STATE_TYPE]: 0,
};

/**
 * Create a room. Sends `com.nurion.channel_type` as `initial_state`
 * when supplied so the room type is visible on the first `/sync`.
 */
export async function createRoom(client: Client, opts: CreateRoomOptions): Promise<string> {
  const isDirect = opts.isDirect ?? false;
  const isPrivate = opts.isPrivate ?? true;
  const visibleToSpaceId = opts.visibleToSpaceId;
  const preset = isDirect ? "trusted_private_chat" : "private_chat";
  const initialState: Array<{ type: string; stateKey?: string; content: unknown }> = [];
  if (opts.channelType) {
    initialState.push({
      type: "com.nurion.channel_type",
      content: { type: opts.channelType },
    });
  }
  if (
    !isDirect &&
    !isPrivate &&
    typeof visibleToSpaceId === "string" &&
    visibleToSpaceId.length > 0
  ) {
    initialState.push({
      type: "m.room.join_rules",
      content: {
        join_rule: "restricted",
        allow: [{ type: "m.room_membership", room_id: visibleToSpaceId }],
      },
    });
  }
  if (isDirect) {
    initialState.push({
      type: "m.room.encryption",
      content: { algorithm: "m.megolm.v1.aes-sha2" },
    });
  }
  return client.bindings.createRoom({
    name: isDirect ? undefined : opts.name,
    isDirect,
    invite: opts.invite ?? [],
    topic: opts.topic,
    preset,
    initialState,
    roomType: opts.roomType,
    ...(opts.roomType === "m.space"
      ? {}
      : { powerLevelContentOverride: { events: { ...CHANNEL_EVENT_POWER_LEVELS } } }),
  });
}

/**
 * Add a child room to a Matrix Space via `m.space.child` state event.
 * `via` lists the servers that can be used to join the child room;
 * typically the homeserver domain extracted from the child room id.
 */
export async function addSpaceChild(
  client: Client,
  spaceId: string,
  childId: string,
  via: string[],
): Promise<void> {
  await client.bindings.sendRoomStateEvent(spaceId, "m.space.child", { via }, childId);
}

/**
 * Remove a child from a Matrix Space by sending an empty
 * `m.space.child` state event (same semantics as redaction for
 * state events with a state key).
 */
export async function removeSpaceChild(
  client: Client,
  spaceId: string,
  childId: string,
): Promise<void> {
  await client.bindings.sendRoomStateEvent(spaceId, "m.space.child", {}, childId);
}

export async function moveRoomToSpace(
  client: Client,
  rooms: RoomSummary[],
  roomId: string,
  spaceId: string | null,
): Promise<void> {
  const currentParents = rooms.filter(
    (room) => room.isSpace && (room.spaceChildRoomIds ?? []).includes(roomId),
  );
  const alreadyInTargetSpace = currentParents.some((parent) => parent.id === spaceId);
  if (spaceId && !alreadyInTargetSpace) {
    const serverName = roomId.split(":")[1] ?? "nurion";
    await addSpaceChild(client, spaceId, roomId, [serverName]);
  }
  for (const parent of currentParents) {
    if (parent.id === spaceId) continue;
    await removeSpaceChild(client, parent.id, roomId);
  }
}

/**
 * Find an existing 1:1 DM with `userId`.
 *
 * Matches only 1:1 DMs (exactly one peer in `directUserIds`). Group DMs
 * that happen to include `userId` alongside others are a different
 * conversation and must not be surfaced as the "DM with X" — reusing a
 * group room for a 1:1 would leak messages across unrelated
 * conversations. Callers who want any-room-containing-user lookup should
 * filter `rooms` directly.
 */
export function findDirectRoom(rooms: RoomSummary[], userId: string): RoomSummary | null {
  for (const room of rooms) {
    if (!room.isDirect) continue;
    if (room.directUserIds.length !== 1) continue;
    if (room.directUserIds[0] !== userId) continue;
    return room;
  }
  return null;
}

/* ── Saved items ─────────────────────────────────────────────────── */

/**
 * A single saved message. `savedAt` is epoch-ms so sort order is
 * deterministic across devices.
 */
export interface SavedItem {
  roomId: string;
  eventId: string;
  savedAt: number;
}

/** Account-data content for `com.nurion.saved.v1`. */
export interface SavedAccountData {
  items: SavedItem[];
}

/**
 * Account-data event type. The trailing `.v1` is the format version so
 * a future migration can ship a `.v2` event and tolerate pre-migration
 * devices still writing to `.v1` (reader-makes-right, not
 * writer-breaks-old). Same pattern Slack, Element, and Matrix itself
 * use for custom account data.
 */
export const SAVED_ACCOUNT_DATA_TYPE = "com.nurion.saved.v1" as const;

export const DND_ACCOUNT_DATA_TYPE = "com.nurion.dnd.v1" as const;

export const DEFAULT_DND_ACCOUNT_DATA: DndAccountData = {
  manual: { until: null },
  schedule: { enabled: false, windows: [] },
};

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isDndDay(value: unknown): value is DndDay {
  return (
    value === 0 ||
    value === 1 ||
    value === 2 ||
    value === 3 ||
    value === 4 ||
    value === 5 ||
    value === 6
  );
}

function normaliseDndAccountData(raw: unknown): DndAccountData {
  if (!raw || typeof raw !== "object") return DEFAULT_DND_ACCOUNT_DATA;
  const input = raw as {
    manual?: { until?: unknown };
    schedule?: { enabled?: unknown; windows?: unknown };
  };
  const manualUntil = input.manual?.until;
  const until =
    typeof manualUntil === "number" && Number.isFinite(manualUntil) && manualUntil > 0
      ? manualUntil
      : null;
  const windows: DndScheduleWindow[] = [];
  if (Array.isArray(input.schedule?.windows)) {
    for (const entry of input.schedule.windows) {
      if (!entry || typeof entry !== "object") continue;
      const candidate = entry as { days?: unknown; start?: unknown; end?: unknown };
      if (!Array.isArray(candidate.days)) continue;
      if (typeof candidate.start !== "string" || !TIME_RE.test(candidate.start)) continue;
      if (typeof candidate.end !== "string" || !TIME_RE.test(candidate.end)) continue;
      const days = candidate.days.filter(isDndDay);
      if (days.length === 0) continue;
      windows.push({ days: Array.from(new Set(days)), start: candidate.start, end: candidate.end });
    }
  }
  return {
    manual: { until },
    schedule: {
      enabled: input.schedule?.enabled === true,
      windows,
    },
  };
}

export async function readDnd(client: Client): Promise<DndAccountData> {
  const raw = await client.bindings.getAccountData<unknown>(DND_ACCOUNT_DATA_TYPE);
  return normaliseDndAccountData(raw);
}

export async function writeDnd(client: Client, data: DndAccountData): Promise<void> {
  await client.bindings.setAccountData<DndAccountData>(DND_ACCOUNT_DATA_TYPE, data);
}

export function subscribeDnd(client: Client, cb: (data: DndAccountData) => void): Unsubscribe {
  return client.bindings.subscribeAccountData<unknown>(DND_ACCOUNT_DATA_TYPE, (raw) => {
    cb(normaliseDndAccountData(raw));
  });
}

/** Hard cap on the number of saved items. Matrix's default max event
 *  size is 64 KiB. Real `roomId` and `eventId` values run ~45-50 UTF-8
 *  bytes each (MXC-style `!localpart:server.example` plus `$…` event
 *  ids), so a saved item serialises to roughly 120 bytes of JSON
 *  before whitespace. 500 items × ~120 B ≈ 60 KB — close to the limit
 *  but deliberately shy of it, so adding a small optional field in a
 *  future `.v2` format (say, a 16-char note or a tag array) stays
 *  under without another cap reduction. If Synapse surfaces a
 *  `M_TOO_LARGE` response the next write will reject and callers see
 *  it as a save failure. */
export const SAVED_ITEMS_CAP = 500;

/**
 * Return the saved-items content normalised to a typed object. Guards
 * against (a) a missing payload, (b) a payload written by a broken
 * client with a non-array `items`, and (c) items that have been
 * corrupted in transit. Unknown fields are dropped; unknown but
 * well-shaped items are preserved so a newer-client write doesn't lose
 * data on this device.
 */
function normaliseSavedAccountData(raw: unknown): SavedAccountData {
  if (!raw || typeof raw !== "object") return { items: [] };
  const items = (raw as { items?: unknown }).items;
  if (!Array.isArray(items)) return { items: [] };
  const out: SavedItem[] = [];
  const seen = new Set<string>();
  for (const entry of items) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Partial<SavedItem>;
    if (typeof e.roomId !== "string" || e.roomId.length === 0) continue;
    if (typeof e.eventId !== "string" || e.eventId.length === 0) continue;
    // `savedAt` must be a positive finite epoch-ms. Negative or zero
    // values fall outside Matrix's event timestamp range and are
    // almost certainly a corrupted write; rejecting them stops a bad
    // entry from surviving the cap eviction (which sorts by savedAt).
    if (typeof e.savedAt !== "number" || !Number.isFinite(e.savedAt) || e.savedAt <= 0) continue;
    // Dedup by eventId — the first occurrence wins. A duplicate in
    // account data should never happen (applySaveItem is idempotent)
    // but a cross-device race with a buggy peer could produce one;
    // deduping here prevents a React key collision downstream and
    // keeps the cap accurate.
    if (seen.has(e.eventId)) continue;
    seen.add(e.eventId);
    out.push({
      roomId: e.roomId,
      eventId: e.eventId,
      savedAt: e.savedAt,
    });
  }
  return { items: out };
}

/** Read the saved-items list. Returns an empty list if the event has
 *  never been written or if the payload is malformed. */
export async function readSavedItems(client: Client): Promise<SavedAccountData> {
  const raw = await client.bindings.getAccountData<unknown>(SAVED_ACCOUNT_DATA_TYPE);
  return normaliseSavedAccountData(raw);
}

/**
 * Result of a mutation against the saved list. `evictedCount > 0` means
 * the cap was exceeded and the N oldest items were dropped — callers
 * surface this as a toast.
 */
export interface SaveMutationResult {
  items: SavedItem[];
  evictedCount: number;
}

/**
 * Compute the next saved list after adding `item`. Pure; callers are
 * responsible for persisting the returned list. The function:
 *   - no-ops if `item.eventId` already exists (idempotent save)
 *   - prepends newest first
 *   - evicts the oldest entries when over the cap
 */
export function applySaveItem(current: SavedItem[], item: SavedItem): SaveMutationResult {
  if (current.some((i) => i.eventId === item.eventId)) {
    return { items: current, evictedCount: 0 };
  }
  // Insert newest-first, then evict oldest when over cap. Sorting in
  // this order means a valid list stays valid even if the incoming
  // `current` was delivered out of order by a peer device.
  const merged = [item, ...current].sort((a, b) => b.savedAt - a.savedAt);
  if (merged.length <= SAVED_ITEMS_CAP) {
    return { items: merged, evictedCount: 0 };
  }
  const trimmed = merged.slice(0, SAVED_ITEMS_CAP);
  return { items: trimmed, evictedCount: merged.length - trimmed.length };
}

/** Compute the next saved list after removing `eventId`. Pure. */
export function applyUnsaveItem(current: SavedItem[], eventId: string): SavedItem[] {
  return current.filter((i) => i.eventId !== eventId);
}

/**
 * Save an event. Merges against the latest account-data snapshot to
 * stay conflict-safe across devices — a concurrent save on another
 * device (visible to us via `/sync`) is preserved even if our in-memory
 * copy predates it. Returns the final list and how many items were
 * evicted by the cap.
 */
export async function saveEvent(
  client: Client,
  params: { roomId: string; eventId: string; now?: number },
): Promise<SaveMutationResult> {
  const now = params.now ?? Date.now();
  // Always re-read before writing: account data is eventually consistent
  // across devices, and the latest version from `/sync` may have
  // additions we haven't merged locally yet. This is the "conflict-safe
  // merge" the ticket calls for.
  const latest = await readSavedItems(client);
  const result = applySaveItem(latest.items, {
    roomId: params.roomId,
    eventId: params.eventId,
    savedAt: now,
  });
  await client.bindings.setAccountData<SavedAccountData>(SAVED_ACCOUNT_DATA_TYPE, {
    items: result.items,
  });
  return result;
}

/**
 * Remove a saved event. Idempotent — un-saving an already-unsaved
 * event skips the account-data write (nothing changed to broadcast)
 * and resolves with the current list. Devices that never saw the item
 * already agree with the outcome, so there's nothing to converge.
 */
export async function unsaveEvent(client: Client, eventId: string): Promise<SavedItem[]> {
  const latest = await readSavedItems(client);
  const next = applyUnsaveItem(latest.items, eventId);
  if (next.length === latest.items.length) return latest.items;
  await client.bindings.setAccountData<SavedAccountData>(SAVED_ACCOUNT_DATA_TYPE, { items: next });
  return next;
}

/**
 * Clear every saved item.
 */
export async function clearSaved(client: Client): Promise<SavedItem[]> {
  const latest = await readSavedItems(client);
  if (latest.items.length === 0) return latest.items;
  await client.bindings.setAccountData<SavedAccountData>(SAVED_ACCOUNT_DATA_TYPE, { items: [] });
  return [];
}

/**
 * Subscribe to the live saved-items list. Fires synchronously with the
 * current (normalised) snapshot and then on every remote or local
 * change. Returns an unsubscribe function.
 */
export function subscribeSavedItems(client: Client, cb: (items: SavedItem[]) => void): Unsubscribe {
  return client.bindings.subscribeAccountData<unknown>(SAVED_ACCOUNT_DATA_TYPE, (raw) => {
    cb(normaliseSavedAccountData(raw).items);
  });
}

/* ── Crypto wrappers ────────────────────────────────────────────── */

/**
 * Initialise the rust crypto store. Must be called before `startSync`
 * — if sync runs first, the initial `/sync` may deliver encrypted
 * events the SDK can't decrypt yet.
 *
 * Idempotent: a second call on a client that already initialised
 * resolves immediately.
 */
export async function initCrypto(client: Client): Promise<void> {
  await client.bindings.initCrypto();
}

/**
 * Bootstrap cross-signing keys (master / user-signing / self-signing).
 * Idempotent — if the keys already exist on the server and locally,
 * this resolves without touching interactive auth. On first run the
 * homeserver may require UIA; `interactiveAuth` is called back with
 * the flow description and must return an `auth` payload.
 */
export async function bootstrapCrossSigning(
  client: Client,
  interactiveAuth: InteractiveAuthCallback,
): Promise<void> {
  await client.bindings.bootstrapCrossSigning(interactiveAuth);
}

/**
 * Ensure Secure Secret Storage (4S) exists. On first run this creates
 * a new storage key and returns the recovery key the UI must display
 * once. On subsequent runs the binding returns an empty object.
 *
 * The recovery key is never persisted by this module — it is the
 * user's responsibility to write it down / save it somewhere safe.
 */
export async function ensureSecretStorage(client: Client): Promise<{ recoveryKey?: string }> {
  return client.bindings.ensureSecretStorage();
}

/**
 * Ensure a server-side key backup exists. Creates one tied to the
 * current secret storage key if missing. Idempotent.
 */
export async function ensureKeyBackup(client: Client): Promise<void> {
  try {
    await client.bindings.ensureKeyBackup();
  } catch (err) {
    if (matrixErrorMessage(err) === "backup_exists_on_server") {
      throw new BackupExistsError();
    }
    throw err;
  }
}

/**
 * Restore historical encrypted message keys from the server backup
 * using a recovery key the user types in. Returns the count of keys
 * recovered when the SDK can report one.
 */
export async function restoreKeyBackupWithRecoveryKey(
  client: Client,
  recoveryKey: string,
): Promise<{ restoredCount: number | null }> {
  if (typeof recoveryKey !== "string" || recoveryKey.length === 0) {
    throw new Error("restoreKeyBackupWithRecoveryKey: recoveryKey must be a non-empty string");
  }
  return client.bindings.restoreKeyBackupWithRecoveryKey(recoveryKey);
}

export async function restoreKeyBackupWithCachedKey(
  client: Client,
): Promise<{ restoredCount: number | null }> {
  return client.bindings.restoreKeyBackupWithCachedKey();
}

export async function restoreKeyBackupFromVerifiedDevice(
  client: Client,
): Promise<VerifiedDeviceRestoreResult> {
  return client.bindings.restoreKeyBackupFromVerifiedDevice();
}

/** Whether this device has the current secret storage key locally.
 *  Used to decide between the "show recovery key" flow (no backup
 *  yet) and the "restore from backup" flow (backup exists, we don't
 *  have the key). */
export async function hasSecretStorageKey(client: Client): Promise<boolean> {
  return client.bindings.hasSecretStorageKey();
}

/** Whether a server-side key backup exists for this user. */
export async function hasKeyBackup(client: Client): Promise<boolean> {
  return client.bindings.hasKeyBackup();
}

/**
 * Subscribe to incoming verification requests. The UI renders a
 * banner from each request; accepting promotes it to a live
 * `VerificationSession` the dialog renders.
 */
export function subscribeVerificationRequests(
  client: Client,
  cb: (req: VerificationRequest) => void,
): Unsubscribe {
  return client.bindings.subscribeVerificationRequests(cb);
}

/** Start an outgoing SAS verification against another device. */
export async function startSasVerification(
  client: Client,
  userId: string,
  deviceId: string,
): Promise<VerificationSession> {
  return client.bindings.startSasVerification(userId, deviceId);
}

export async function startVerificationRequest(
  client: Client,
  userId: string,
  deviceId: string,
): Promise<VerificationRequest> {
  return client.bindings.startVerificationRequest(userId, deviceId);
}

export async function getOwnDevices(client: Client): Promise<MatrixDevice[]> {
  return client.bindings.getOwnDevices();
}

export function getDeviceId(client: Client): string | null {
  return client.bindings.getDeviceId();
}

export async function getUserDevices(client: Client, userId: string): Promise<MatrixDevice[]> {
  if (!userId.startsWith("@")) {
    throw new Error("getUserDevices: userId must be a Matrix user id (e.g. @alice:server)");
  }
  return client.bindings.getUserDevices(userId);
}

/** Classify an event for badge rendering. Unencrypted events always
 *  map to `unencrypted`; decryption failures map to `undecryptable`. */
export function getMessageEncryptionStatus(
  client: Client,
  event: MessageEventLike,
): MessageEncryptionStatus {
  return client.bindings.getMessageEncryptionStatus(event);
}

/**
 * Restore the session key for a specific UTD event from server-side backup.
 */
export async function requestKeysForEvent(
  client: Client,
  roomId: string,
  eventId: string,
): Promise<void> {
  await client.bindings.requestKeysForEvent(roomId, eventId);
}

/** Whether a given room is end-to-end encrypted (per `m.room.encryption`). */
export async function isRoomEncrypted(client: Client, roomId: string): Promise<boolean> {
  return client.bindings.isRoomEncrypted(roomId);
}

/**
 * Subscribe to decryption reports from the SDK's event-cache
 * redecryptor. Use this to invalidate timeline caches when
 * UTD events become decryptable in the background — late
 * `m.room_key` to-device delivery, a restored key backup, or an
 * explicit `requestKeysForEvent`. Returns an `Unsubscribe`.
 */
export function subscribeDecryptionReports(
  client: Client,
  cb: DecryptionReportSubscriber,
): Unsubscribe {
  return client.bindings.subscribeDecryptionReports(cb);
}

/* ── Timeline ────────────────────────────────────────────────────── */

/**
 * Subscribe to `roomId`'s timeline. The callback fires synchronously
 * with the current buffer and then on every change. Returns an
 * unsubscribe function — callers must invoke it when the room changes
 * or the component unmounts.
 */
export function subscribeTimeline(
  client: Client,
  roomId: string,
  cb: TimelineSubscriber,
  opts?: TimelineSubscriptionOptions,
): Unsubscribe {
  return client.bindings.subscribeTimeline(roomId, cb, opts);
}

/**
 * Load up to `limit` older events. Emits through the timeline
 * subscription. `atStart=true` once the server confirms no more
 * history — the UI suppresses further requests.
 */
export async function paginateBackward(
  client: Client,
  roomId: string,
  limit: number,
): Promise<PaginateBackwardResult> {
  if (!Number.isFinite(limit) || limit <= 0) {
    throw new Error("paginateBackward: limit must be a positive number");
  }
  return client.bindings.paginateBackward(roomId, limit);
}

/**
 * Fetch one earlier page of a thread's replies. The initial
 * `subscribeThread` loads only the newest page; this advances the
 * per-thread cursor by exactly one page and merges the older events
 * into the retained window without refetching. Returns the same room
 * backward contract (`{ loaded, atStart }`), where `atStart` marks the
 * absent continuation that terminates paging. Validates the ids before
 * delegating so a bad id fails fast instead of reaching the binding.
 */
export async function paginateThreadBackward(
  client: Client,
  roomId: string,
  rootEventId: string,
  limit: number,
): Promise<PaginateBackwardResult> {
  if (!client.bindings) throw new Error("Matrix is not ready");
  if (!roomId.trim()) throw new Error("roomId is required");
  if (!rootEventId.trim()) throw new Error("rootEventId is required");
  if (limit < 1 || !Number.isInteger(limit)) {
    throw new Error("paginateThreadBackward requires a positive integer limit");
  }
  return client.bindings.paginateThreadBackward(roomId, rootEventId, limit);
}

/**
 * Fetch a single event by id without paginating surrounding context.
 * Used for historical pinned-message previews: the current timeline
 * buffer remains the fast path, and missing pins can resolve directly.
 */
export async function getRoomEvent(
  client: Client,
  roomId: string,
  eventId: string,
): Promise<TimelineEvent | null> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("getRoomEvent: roomId must be a non-empty string");
  }
  if (typeof eventId !== "string" || eventId.length === 0) {
    throw new Error("getRoomEvent: eventId must be a non-empty string");
  }
  return client.bindings.getRoomEvent(roomId, eventId);
}

/**
 * Send a plain-text message to `roomId`. Empty / whitespace-only bodies
 * are rejected at the wrapper — the composer already disables send, but
 * a defensive guard keeps the SDK from receiving garbage from future
 * call sites.
 *
 * `formattedBody` is optional Matrix-custom HTML emitted alongside the
 * plain-text body when the composer serialises mention pills (or any
 * future formatting). The wrapper drops it when it equals the plain
 * body or is whitespace-only — sending an HTML payload identical to
 * the plain body just bloats the event without changing render.
 */
export async function sendText(
  client: Client,
  roomId: string,
  body: string,
  formattedBody?: string,
  txnId?: string,
  mentions?: string[],
  mentionRoom?: boolean,
): Promise<string> {
  const trimmed = body.trim();
  if (trimmed.length === 0) {
    throw new Error("sendText: body must contain non-whitespace content");
  }
  // Drop the formatted body when it has no content beyond whitespace
  // OR when it's byte-identical to `body` — the latter happens for
  // belt-and-braces call sites that pass `formattedBody = body` on a
  // composition with no real markup, and emitting a redundant
  // `formatted_body` just bloats the event without changing render
  // for any spec-compliant client. Today's composer never produces
  // that exact shape (mention pills always inject `<a>`), but the
  // guard keeps future call sites honest.
  const html =
    typeof formattedBody === "string" && formattedBody.trim().length > 0 && formattedBody !== body
      ? formattedBody
      : undefined;
  const userIds = mentions !== undefined && mentions.length > 0 ? mentions : undefined;
  return client.bindings.sendText(roomId, body, html, txnId, userIds, mentionRoom);
}

/** Send a Matrix-standard emote message (`m.emote`) to `roomId`. */
export async function sendEmote(client: Client, roomId: string, body: string): Promise<string> {
  const trimmed = body.trim();
  if (trimmed.length === 0) {
    throw new Error("sendEmote: body must contain non-whitespace content");
  }
  return client.bindings.sendEmote(roomId, trimmed);
}

/**
 * Re-drive a failed local echo in place. Used by the mobile
 * tap-to-retry affordance: the failed `eventId` transitions
 * `failed -> sending -> sent` on the same row instead of enqueuing a
 * duplicate send.
 */
export async function resendLocalEcho(
  client: Client,
  roomId: string,
  eventId: string,
): Promise<string> {
  return client.bindings.resendLocalEcho(roomId, eventId);
}

/** Scheduled-message dispatcher entry point. Reuses the normal text
 *  wrapper so scheduled sends get the same validation and formatted-body
 *  normalisation as immediate composer sends. */
export async function sendScheduledText(
  client: Client,
  roomId: string,
  body: string,
  formattedBody?: string,
  txnId?: string,
): Promise<string> {
  return sendText(client, roomId, body, formattedBody, txnId);
}

/**
 * Mark `eventId` as read in `roomId`. Best-effort: the contract
 * swallows SDK errors so call sites can `void sendReadReceipt(...)`
 * without risking unhandled rejections. The server clears the unread
 * count for everything up to and including this event.
 */
export async function sendReadReceipt(
  client: Client,
  roomId: string,
  eventId: string,
): Promise<void> {
  try {
    await client.bindings.sendReadReceipt(roomId, eventId);
  } catch (err) {
    console.warn("sendReadReceipt failed", err);
  }
}

/* ── Link previews ──────────────────────────────────────────────── */

/**
 * Fetch an OpenGraph preview for `url` via Synapse's `/preview_url`
 * endpoint. Returns `null` on any error — this is the documented
 * fail-silent contract the UI relies on (a failed preview renders
 * nothing, never a broken tombstone). Non-http(s) URLs are rejected
 * before hitting the server: Synapse refuses them anyway, and the
 * wrapper wants the `null` decision immediate so the hook can cache
 * negative lookups without a round trip.
 */
export async function getUrlPreview(
  client: Client,
  url: string,
  ts?: number,
): Promise<LinkPreview | null> {
  if (typeof url !== "string" || url.length === 0) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  } catch {
    return null;
  }
  try {
    return await client.bindings.getUrlPreview(url, ts);
  } catch {
    // Binding contract is `resolves with null on error`, but a thrown
    // exception from a future SDK would otherwise bubble and crash the
    // timeline on a single bad URL. Guard defensively.
    return null;
  }
}

/* ── Media upload & attachment sending ──────────────────────────── */

/**
 * Upload a blob to the Matrix media repository. Thin wrapper over the
 * binding call; exposed as a module function so callers don't reach
 * through `client.bindings` directly. `onProgress` receives an integer
 * percentage in `[0, 100]`. Pass `signal` to allow the caller to abort
 * the upload — in-flight aborts reject with `DOMException('AbortError')`.
 */
export async function uploadContent(
  client: Client,
  blob: Blob,
  opts?: { onProgress?: (pct: number) => void; signal?: AbortSignal },
): Promise<{ mxc: string }> {
  if (!(blob instanceof Blob)) {
    throw new Error("uploadContent: blob must be a Blob");
  }
  return client.bindings.uploadContent(blob, opts);
}

// `mxcToHttp` was previously exported from this module as a thin
// wrapper over `client.bindings.mxcToHttp`. It is intentionally
// removed in #3147: Synapse ≥ 1.120 ships with
// `enable_authenticated_media: true` (and our pinned
// `matrixdotorg/synapse:v1.152.0` runs that default), so the v3
// unauthenticated URL the wrapper produced returns 404 for every
// post-toggle upload. The v1 authenticated path requires an
// `Authorization: Bearer` header which `<img src=…>` cannot supply.
//
// Production consumers must use `useAuthenticatedMedia` from
// `~/hooks/useAuthenticatedMedia` — it fetches the bytes through
// `downloadMedia` (the SDK attaches the access token) and feeds an
// `URL.createObjectURL` into the `<img>`. Removing the export
// rather than leaving a runtime-throwing stub turns any remaining
// call site into a TypeScript compile-time error, which is the
// fail-fast behaviour we want.
//
// The Rust binding's `mxcToHttp` likewise throws (see
// `matrix-sdk-rust.ts`) so an `as any` route into the binding
// surface still fails noisily; the stub's `mxcToHttp` keeps
// working because Storybook fixtures never round-trip through
// Synapse.

/* ── Threads API ─────────────────────────────────────────────── */

/**
 * Subscribe to the ordered list of replies in a thread. The subscriber
 * is invoked with the current snapshot once synchronously (where the
 * SDK allows) and on every change.
 */
export function subscribeThread(
  client: Client,
  roomId: string,
  rootEventId: string,
  cb: ThreadSubscriber,
): Unsubscribe {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("subscribeThread: roomId must be a non-empty string");
  }
  if (typeof rootEventId !== "string" || rootEventId.length === 0) {
    throw new Error("subscribeThread: rootEventId must be a non-empty string");
  }
  return client.bindings.subscribeThread(roomId, rootEventId, cb);
}

/**
 * Send a reply into a thread rooted at `rootEventId`. `latestReplyId`
 * is optional — when absent, the root event id is used for the
 * `m.in_reply_to` fallback, which is what the spec requires for the
 * first reply in a thread.
 *
 * Returns the new event id on success. The caller should not assume
 * the new reply is already visible via `subscribeThread` on return —
 * the SDK may round-trip through sync before emitting it.
 */
export async function sendThreadReply(
  client: Client,
  roomId: string,
  rootEventId: string,
  body: string,
  latestReplyId?: string,
  options?: ThreadReplyOptions,
): Promise<string> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("sendThreadReply: roomId must be a non-empty string");
  }
  if (typeof rootEventId !== "string" || rootEventId.length === 0) {
    throw new Error("sendThreadReply: rootEventId must be a non-empty string");
  }
  const trimmed = body.trim();
  if (trimmed.length === 0) {
    throw new Error("sendThreadReply: body must be non-empty");
  }
  const inReplyTo = latestReplyId ?? rootEventId;
  if (!hasRichThreadOptions(options)) {
    return client.bindings.sendThreadReply(roomId, rootEventId, trimmed, inReplyTo);
  }
  return client.bindings.sendThreadReply(roomId, rootEventId, trimmed, inReplyTo, options);
}

/** Mark a thread read up to `eventId` without moving the room read marker. */
export async function sendThreadReadReceipt(
  client: Client,
  roomId: string,
  rootEventId: string,
  eventId: string,
): Promise<void> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("sendThreadReadReceipt: roomId must be a non-empty string");
  }
  if (typeof rootEventId !== "string" || rootEventId.length === 0) {
    throw new Error("sendThreadReadReceipt: rootEventId must be a non-empty string");
  }
  if (typeof eventId !== "string" || eventId.length === 0) {
    throw new Error("sendThreadReadReceipt: eventId must be a non-empty string");
  }
  await client.bindings.sendThreadReadReceipt(roomId, rootEventId, eventId);
}

/**
 * Minimum fields for an `m.image`. Mirrors the Matrix spec: `info.w`,
 * `info.h`, `info.size`, `info.mimetype` sit under `info`; `body` is
 * the filename (fallback display name); `url` is the mxc.
 */
export async function sendImage(
  client: Client,
  roomId: string,
  msg: SendImageMessage,
): Promise<string> {
  if (!(msg.data instanceof Blob)) {
    throw new Error("sendImage: data must be a Blob");
  }
  return client.bindings.sendImage(roomId, msg);
}

/** Send an `m.video` message. Video playback is intentionally absent
 *  from the UI — the timeline renders a download tile until we ship a
 *  player. */
export async function sendVideo(
  client: Client,
  roomId: string,
  msg: SendVideoMessage,
): Promise<string> {
  if (!(msg.data instanceof Blob)) {
    throw new Error("sendVideo: data must be a Blob");
  }
  return client.bindings.sendVideo(roomId, msg);
}

/** Send an `m.file` message for any non-image / non-video attachment. */
export async function sendFile(
  client: Client,
  roomId: string,
  msg: SendBinaryMediaMessage,
): Promise<string> {
  if (!(msg.data instanceof Blob)) {
    throw new Error("sendFile: data must be a Blob");
  }
  return client.bindings.sendFile(roomId, msg);
}

/* ── Presence / typing / receipts ────────────────────────────────── */

/**
 * Subscribe to presence updates for `userId`. The subscriber fires
 * synchronously with the current (best-effort) snapshot and then on
 * every change. `userId` may be any Matrix id — rooms the local user
 * doesn't share with them return `offline` with `lastActive=null`.
 *
 * Callers receive a typed `Unsubscribe`; component cleanup is a
 * straight call-site — no additional lifecycle to reason about.
 */
export function subscribePresence(
  client: Client,
  userId: string,
  cb: PresenceSubscriber,
): Unsubscribe {
  return client.bindings.subscribePresence(userId, cb);
}

function normaliseMeetingPresence(raw: unknown): MeetingPresence | null {
  if (!raw || typeof raw !== "object") return null;
  const input = raw as {
    meetingId?: unknown;
    title?: unknown;
    startedAt?: unknown;
    joinUrl?: unknown;
    matrixRoomId?: unknown;
    joinCapability?: unknown;
    hostUserId?: unknown;
    active?: unknown;
  };
  // The inactive tombstone (`active: false`) and any legacy/missing
  // marker resolve to "no meeting".
  if (input.active !== true) return null;
  if (typeof input.meetingId !== "string" || input.meetingId.length === 0) return null;
  if (typeof input.title !== "string" || input.title.length === 0) return null;
  if (typeof input.startedAt !== "string" || input.startedAt.length === 0) return null;
  if (typeof input.joinUrl !== "string" || input.joinUrl.length === 0) return null;
  if (typeof input.matrixRoomId !== "string" || input.matrixRoomId.length === 0) return null;
  if (typeof input.joinCapability !== "string" || input.joinCapability.length === 0) return null;
  return {
    meetingId: input.meetingId,
    title: input.title,
    startedAt: input.startedAt,
    joinUrl: input.joinUrl,
    matrixRoomId: input.matrixRoomId,
    joinCapability: input.joinCapability,
    hostUserId:
      typeof input.hostUserId === "string" && input.hostUserId.length > 0
        ? input.hostUserId
        : undefined,
    active: true,
  };
}

export function subscribeMeetingActive(
  client: Client,
  roomId: string,
  cb: (meeting: MeetingPresence | null) => void,
): Unsubscribe {
  return client.bindings.subscribeRoomStateEvent<unknown>(
    roomId,
    MEETING_ACTIVE_STATE_TYPE,
    undefined,
    (raw) => cb(normaliseMeetingPresence(raw)),
  );
}

/** Content written to the `com.nurion.meeting_active` state event when a
 *  meeting starts. The booking-minted `joinCapability` lives here, in
 *  member-only-readable room state. */
export interface MeetingActiveInput {
  meetingId: string;
  title: string;
  startedAt: string;
  joinUrl: string;
  matrixRoomId: string;
  joinCapability: string;
  hostUserId?: string;
}

/** Light the meeting banner for all room members. Requires state-write
 *  power for `MEETING_ACTIVE_STATE_TYPE`; the SDK re-throws on denial. */
export async function setMeetingActive(
  client: Client,
  roomId: string,
  meeting: MeetingActiveInput,
): Promise<void> {
  await client.bindings.sendRoomStateEvent(roomId, MEETING_ACTIVE_STATE_TYPE, {
    ...meeting,
    active: true,
  });
}

/** Write the inactive tombstone so the banner disappears and any stale
 *  capability in the previous content is no longer surfaced. */
export async function clearMeetingActive(client: Client, roomId: string): Promise<void> {
  await client.bindings.sendRoomStateEvent(roomId, MEETING_ACTIVE_STATE_TYPE, { active: false });
}

export async function ensureMeetingActivePowerLevel(client: Client, roomId: string): Promise<void> {
  const powerLevels = await getRoomPowerLevels(client, roomId);
  const events = powerLevels.events ?? {};
  if (events[MEETING_ACTIVE_STATE_TYPE] !== undefined) return;
  await setRoomPowerLevels(client, roomId, {
    ...powerLevels,
    events: { ...events, [MEETING_ACTIVE_STATE_TYPE]: 0 },
  });
}

/** Snapshot current presence without installing a subscription. */
export function getUserPresence(client: Client, userId: string): PresenceSnapshot {
  return client.bindings.getUserPresence(userId);
}

/** Best-effort read of custom user-scoped data for another visible user. */
export async function getUserAccountDataLike<T>(
  client: Client,
  userId: string,
  eventType: string,
): Promise<T | null> {
  if (!userId.startsWith("@")) {
    throw new Error("getUserAccountDataLike: userId must be a Matrix user id (e.g. @alice:server)");
  }
  return client.bindings.getUserAccountDataLike<T>(userId, eventType);
}

/** Set the local user's Matrix presence state. */
export async function setPresence(client: Client, state: PresenceState): Promise<void> {
  if (state !== "online" && state !== "unavailable" && state !== "offline") {
    throw new Error("setPresence: state must be online, unavailable, or offline");
  }
  await client.bindings.setPresence(state);
}

export function normaliseUserStatus(value: unknown, now = Date.now()): UserStatus | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<UserStatusAccountData>;
  const emoji = typeof raw.emoji === "string" ? raw.emoji.trim() : "";
  const message = typeof raw.message === "string" ? raw.message.trim() : "";
  const expiresAt = raw.expiresAt === null || raw.expiresAt === undefined ? null : raw.expiresAt;
  if (emoji.length === 0 || message.length === 0) return null;
  if (expiresAt !== null && !Number.isFinite(expiresAt)) return null;
  if (expiresAt !== null && expiresAt <= now) return { emoji, message, expiresAt };
  return { emoji, message, expiresAt };
}

export async function readMyStatus(client: Client): Promise<UserStatus | null> {
  const raw = await client.bindings.getAccountData<unknown>(USER_STATUS_EVENT_TYPE);
  return normaliseUserStatus(raw);
}

export async function getUserStatus(client: Client, userId: string): Promise<UserStatus | null> {
  const raw = await client.bindings.getUserAccountDataLike<unknown>(userId, USER_STATUS_EVENT_TYPE);
  return normaliseUserStatus(raw);
}

/** Presence may be disabled server-side, so a mirror failure must not fail the durable account-data write that is the source of truth for status. */
async function mirrorPresenceStatus(client: Client, statusMsg: string | null): Promise<void> {
  try {
    await client.bindings.setPresenceStatus(statusMsg);
  } catch (err) {
    console.warn("setPresenceStatus failed", err);
  }
}

export async function setMyStatus(client: Client, status: UserStatus | null): Promise<void> {
  if (!status) {
    await client.bindings.setAccountData<UserStatusAccountData>(USER_STATUS_EVENT_TYPE, {});
    await mirrorPresenceStatus(client, null);
    return;
  }
  const content: UserStatusAccountData = {
    emoji: status.emoji,
    message: status.message,
    statusMsg: `${status.emoji} ${status.message}`,
    expiresAt: status.expiresAt,
  };
  await client.bindings.setAccountData<UserStatusAccountData>(USER_STATUS_EVENT_TYPE, content);
  await mirrorPresenceStatus(client, content.statusMsg ?? null);
}

export function subscribeMyStatus(
  client: Client,
  cb: (status: UserStatus | null) => void,
): Unsubscribe {
  return client.bindings.subscribeAccountData<unknown>(USER_STATUS_EVENT_TYPE, (raw) => {
    cb(normaliseUserStatus(raw));
  });
}

/**
 * Subscribe to the read-marker (`m.fully_read`) for `roomId`. Fires once
 * synchronously with the current marker, then on every `/sync` update.
 * Returns an unsubscribe function. Wraps the binding directly so callers
 * do not reach into `client.bindings`; the semantics live there.
 */
export function subscribeReadMarker(
  client: Client,
  roomId: string,
  cb: ReadMarkerSubscriber,
): Unsubscribe {
  return client.bindings.subscribeReadMarker(roomId, cb);
}

/**
 * Subscribe to the typing-users list for `roomId`. The local user is
 * always excluded from the list — the composer already knows its own
 * state and rendering "You are typing…" under the user's own cursor
 * would be noise. Returns an unsubscribe function.
 */
export function subscribeTyping(client: Client, roomId: string, cb: TypingSubscriber): Unsubscribe {
  return client.bindings.subscribeTyping(roomId, cb);
}

/**
 * Broadcast the local user's typing state. Matrix's typing event
 * carries a server-side expiry (`timeout`); peers automatically
 * clear the indicator after that window if no refresh arrives.
 * Callers are expected to debounce refreshes — the composer fires
 * once per 10s while the user is actively typing, and `false` on
 * clear / submit.
 *
 * Best-effort: swallows SDK errors so call sites can `void` the
 * returned promise without risking unhandled rejections on a brief
 * network blip.
 */
export async function setTyping(
  client: Client,
  roomId: string,
  typing: boolean,
  timeoutMs?: number,
): Promise<void> {
  try {
    await client.bindings.setTyping(roomId, typing, timeoutMs);
  } catch (err) {
    console.warn("setTyping failed", err);
  }
}

/**
 * Read the receipts attached to a specific timeline event. Used by
 * the `ReadReceipts` avatar stack. The local user's own receipt is
 * intentionally excluded — the brief says "own receipts are omitted
 * from the stack", so the contract matches the UI semantics.
 */
export function getReceiptsForEvent(
  client: Client,
  roomId: string,
  eventId: string,
  threadRootEventId?: string,
): Receipt[] {
  return client.bindings.getReceiptsForEvent(roomId, eventId, threadRootEventId);
}

/**
 * Subscribe to receipt-update notifications for a room. The
 * subscriber fires whenever any event's receipt list changes in
 * `roomId`. The callback carries no payload — it's a coarse signal
 * that tells the consumer to re-read receipts via
 * `getReceiptsForEvent`. Returns an unsubscribe function.
 */
export function subscribeReceipts(
  client: Client,
  roomId: string,
  cb: ReceiptsSubscriber,
): Unsubscribe {
  return client.bindings.subscribeReceipts(roomId, cb);
}

/* ── Room administration (#1310) ─────────────────────────────────── */

/**
 * Rename a room. Empty names are rejected here so a malformed round
 * trip can't leave the server with a blank `m.room.name`.
 */
export async function setRoomName(client: Client, roomId: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    throw new Error("setRoomName: name must be non-empty");
  }
  await client.bindings.setRoomName(roomId, trimmed);
}

/**
 * Update a room topic. Empty topics clear the topic — we pass through
 * whatever the caller gave us after trimming, letting the server apply
 * its own rules.
 */
export async function setRoomTopic(client: Client, roomId: string, topic: string): Promise<void> {
  await client.bindings.setRoomTopic(roomId, topic.trim());
}

/**
 * Set a room avatar. The caller uploads first via `uploadContent`, then
 * passes the returned `mxc://` URI here.
 */
export async function setRoomAvatar(client: Client, roomId: string, mxcUri: string): Promise<void> {
  if (!mxcUri.startsWith("mxc://")) {
    throw new Error("setRoomAvatar: mxcUri must start with mxc://");
  }
  await client.bindings.setRoomAvatar(roomId, mxcUri);
}

/** Set or remove the signed-in user's profile photo; upload first via `uploadContent`. */
export async function setOwnAvatar(client: Client, mxcUri: string | null): Promise<void> {
  if (mxcUri !== null && !mxcUri.startsWith("mxc://")) {
    throw new Error("setOwnAvatar: mxcUri must start with mxc://");
  }
  await client.bindings.setOwnAvatar(mxcUri);
}

/**
 * Can `userId` notify the whole room (the `@room` / Slack `@channel`
 * affordance)? Synchronous — the Matrix SDK carries the
 * `m.room.power_levels` snapshot locally after the first `/sync`, so a
 * render-time check never needs to await. Compares the user's power
 * level against `notifications.room` (default 50 per spec). Only a
 * privileged sender is offered the `@room` suggestion.
 */
export function canMentionRoom(client: Client, roomId: string, userId: string): boolean {
  return client.bindings.canMentionRoom(roomId, userId);
}

/**
 * Snapshot the room's current member list. Joined + invited, never
 * `leave` or `ban` entries (the list is "who is in the room right
 * now?").
 */
export function getMembers(client: Client, roomId: string): RoomMember[] {
  return client.bindings.getMembers(roomId);
}

export function canSendStateEvent(
  client: Client,
  roomId: string,
  userId: string,
  eventType: string,
): boolean {
  return client.bindings.canSendStateEvent(roomId, userId, eventType);
}

export function getCachedMember(client: Client, roomId: string, userId: string): RoomMember | null {
  return client.bindings.getCachedMember(roomId, userId);
}

/**
 * Awaited room member load. Resolves once the transport returns the
 * full roster instead of peeking the possibly-empty sync cache, so a
 * DM's org eligibility decides off real membership. Errors propagate so
 * the caller surfaces a retry rather than a silently empty roster.
 */
export function getMemberLoadState(client: Client, roomId: string): MemberLoadState {
  return client.bindings.getMemberLoadState(roomId);
}

export function loadMembers(client: Client, roomId: string): Promise<RoomMember[]> {
  return client.bindings.loadMembers(roomId);
}

/**
 * Send an arbitrary `m.room.message` payload with local echo. Validates
 * that the content is a non-empty `m.text` message before handing it to
 * the binding. Agent slash commands build their contract content here so
 * an agent receives `com.nurion.command` even in mixed rooms.
 */
export async function sendRawMessage(
  client: Client,
  roomId: string,
  content: Record<string, unknown>,
  txnId?: string,
): Promise<string> {
  if (content.msgtype !== "m.text") {
    throw new Error("sendRawMessage: content.msgtype must be 'm.text'");
  }
  const body = content.body;
  if (typeof body !== "string" || body.trim().length === 0) {
    throw new Error("sendRawMessage: content.body must be a non-empty string");
  }
  return client.bindings.sendRawMessage(roomId, content, txnId);
}

export function sendActionResponse(
  client: Client,
  roomId: string,
  eventId: string,
  actionId: string,
): Promise<string> {
  return client.bindings.sendRawEvent(roomId, ACTION_RESPONSE_EVENT_TYPE, {
    version: 1,
    action_id: actionId,
    "m.relates_to": { rel_type: "m.reference", event_id: eventId },
  });
}

export const INVITE_NEEDS_VERIFIED_DEVICE =
  "Verify this device to invite people. Use your other device or your recovery key.";

/** Invite `userId` to `roomId`. The homeserver validates permissions. */
export async function inviteUser(client: Client, roomId: string, userId: string): Promise<void> {
  if (!userId.startsWith("@")) {
    throw new Error("inviteUser: userId must be a Matrix user id (e.g. @alice:server)");
  }
  try {
    await client.bindings.inviteUser(roomId, userId);
  } catch (err) {
    let message = "";
    if (err instanceof Error) {
      message = err.message;
    } else if (
      typeof err === "object" &&
      err !== null &&
      "message" in err &&
      typeof err.message === "string"
    ) {
      message = err.message;
    }
    if (message.includes("your device is not verified")) {
      throw new Error(INVITE_NEEDS_VERIFIED_DEVICE);
    }
    throw err;
  }
}

/**
 * Remove a user from a room. `reason` is visible to the kicked user
 * on their next `/sync`; callers typically leave it empty.
 */
export async function kickUser(
  client: Client,
  roomId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await client.bindings.kickUser(roomId, userId, reason);
}

/** Ban `userId` from `roomId` with an optional reason. */
export async function banUser(
  client: Client,
  roomId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  await client.bindings.banUser(roomId, userId, reason);
}

/** Leave the room as the current user. */
export async function leaveRoom(client: Client, roomId: string): Promise<void> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("leaveRoom: roomId must be a non-empty string");
  }
  await client.bindings.leaveRoom(roomId);
  await client.bindings.forgetRoom(roomId);
}

export async function acceptInvite(
  client: Client,
  roomId: string,
  opts?: { isDirect: boolean; inviter: string },
): Promise<void> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("acceptInvite: roomId must be a non-empty string");
  }
  await client.bindings.acceptInvite(roomId);
  // The join already succeeded; a failed m.direct write must not reject it.
  if (opts?.isDirect && opts.inviter.length > 0) {
    await addDirectRoom(client, opts.inviter, roomId).catch((err) => {
      console.warn("acceptInvite: m.direct pairing write failed", err);
    });
  }
}

export async function declineInvite(client: Client, roomId: string): Promise<void> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("declineInvite: roomId must be a non-empty string");
  }
  await client.bindings.declineInvite(roomId);
}

/** Promote or demote a member by writing their Matrix power level. */
export async function setMemberPowerLevel(
  client: Client,
  roomId: string,
  userId: string,
  powerLevel: number,
): Promise<void> {
  if (!userId.startsWith("@")) {
    throw new Error("setMemberPowerLevel: userId must be a Matrix user id");
  }
  if (!Number.isInteger(powerLevel) || powerLevel > 100) {
    throw new Error("setMemberPowerLevel: powerLevel must be an integer up to 100");
  }
  await client.bindings.setMemberPowerLevel(roomId, userId, powerLevel);
}

/**
 * Search the homeserver user directory. Empty / whitespace-only
 * terms return an empty list synchronously to avoid an unnecessary
 * round-trip on the first keystroke.
 */
export async function searchUserDirectory(
  client: Client,
  term: string,
): Promise<UserDirectoryEntry[]> {
  const trimmed = term.trim();
  if (trimmed.length === 0) return [];
  return client.bindings.searchUserDirectory(trimmed);
}

/**
 * Server-side public-rooms search. Empty / whitespace-only terms short-circuit with an
 * empty list — same reasoning as `searchUserDirectory`.
 */
export async function searchRooms(client: Client, term: string): Promise<RoomSearchResult[]> {
  const trimmed = term.trim();
  if (trimmed.length === 0) return [];
  const targetCount = 10;
  const maxPages = 5;
  const matches: RoomSearchResult[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < maxPages && matches.length < targetCount; page += 1) {
    const result = await client.bindings.searchRooms(trimmed, { limit: targetCount, cursor });
    for (const room of result.results) {
      matches.push(room);
      if (matches.length >= targetCount) break;
    }
    if (result.nextBatch === undefined) break;
    cursor = result.nextBatch;
  }

  return matches;
}

/* ── m.direct ────────────────────────────────────────────────────── */

/**
 * Account-data event that records the user's direct-message pairings.
 * Matrix's `m.direct` maps `{ userId: string[] }` where each array
 * lists the room ids the user has marked as a DM with that person.
 */
export const DIRECT_ACCOUNT_DATA_TYPE = "m.direct" as const;

/** Raw content shape Matrix spec publishes for `m.direct`. */
export type DirectAccountData = Record<string, string[]>;

/**
 * Normalise whatever the server returned to a `Record<string, string[]>`.
 * A malformed payload from another client must not crash our reads;
 * we drop entries that don't match the expected shape.
 */
function normaliseDirect(raw: unknown): DirectAccountData {
  if (!raw || typeof raw !== "object") return {};
  const out: DirectAccountData = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key.startsWith("@")) continue;
    if (!Array.isArray(value)) continue;
    const rooms = value.filter((v): v is string => typeof v === "string" && v.length > 0);
    if (rooms.length === 0) continue;
    out[key] = rooms;
  }
  return out;
}

/**
 * Add `roomId` to every selected user's `m.direct` pairing in one
 * account-data update. Matrix offers no conditional account-data write,
 * so concurrent updates from different devices remain last-write-wins.
 */
export async function addDirectRooms(
  client: Client,
  userIds: string[],
  roomId: string,
): Promise<void> {
  const peers = [...new Set(userIds)];
  if (peers.length === 0 || peers.some((userId) => !userId.startsWith("@"))) {
    throw new Error("addDirectRooms: userIds must contain Matrix user ids");
  }
  if (roomId.length === 0) {
    throw new Error("addDirectRooms: roomId must be non-empty");
  }
  const raw = await client.bindings.getAccountData<unknown>(DIRECT_ACCOUNT_DATA_TYPE);
  const current = normaliseDirect(raw);
  const next: DirectAccountData = { ...current };
  let changed = false;
  for (const userId of peers) {
    const existing = current[userId] ?? [];
    if (existing.includes(roomId)) continue;
    next[userId] = [...existing, roomId];
    changed = true;
  }
  if (!changed) return;
  await client.bindings.setAccountData<DirectAccountData>(DIRECT_ACCOUNT_DATA_TYPE, next);
}

export function addDirectRoom(client: Client, userId: string, roomId: string): Promise<void> {
  return addDirectRooms(client, [userId], roomId);
}

/* ── Push rules / notifications (#1309) ──────────────────────────── */

/**
 * Read the push-rule-evaluated actions for `eventId`. `null` when
 * the event is unknown or the SDK hasn't evaluated push rules yet —
 * callers must treat `null` as "do nothing", never as a default
 * `notify: true`.
 */
export function getPushActionsForEvent(client: Client, eventId: string): PushActions | null {
  if (typeof eventId !== "string" || eventId.length === 0) return null;
  return client.bindings.getPushActionsForEvent(eventId);
}

/**
 * UI-level per-room notification level, derived from the server-side
 * push-rule override. Defaults to `"mentions"` when no override is
 * set — the Matrix default where global rules (e.g. DMs, @mentions)
 * still fire but per-room chatter does not.
 */
export function getRoomNotificationLevel(client: Client, roomId: string): RoomNotificationLevel {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("getRoomNotificationLevel: roomId must be a non-empty string");
  }
  return client.bindings.getRoomNotificationLevel(roomId);
}

/**
 * Persist the per-room notification level. `"mentions"` clears any
 * existing override so the global rules govern. `"all"` and
 * `"mute"` install an override that wins over global rules for
 * this room.
 */
export async function setRoomNotificationLevel(
  client: Client,
  roomId: string,
  level: RoomNotificationLevel,
): Promise<void> {
  if (typeof roomId !== "string" || roomId.length === 0) {
    throw new Error("setRoomNotificationLevel: roomId must be a non-empty string");
  }
  if (level !== "all" && level !== "mentions" && level !== "mute") {
    throw new Error(
      `setRoomNotificationLevel: level must be 'all' | 'mentions' | 'mute', got ${JSON.stringify(level)}`,
    );
  }
  await client.bindings.setRoomNotificationLevel(roomId, level);
}

/**
 * Subscribe to aggregate unread / highlight counts across every
 * joined room. The subscriber fires once synchronously with the
 * current snapshot and on every change. Callers render the tab
 * title (`highlight`) and favicon badge (`total`) from this single
 * source.
 */
export function subscribeTotalUnread(client: Client, cb: UnreadCountsSubscriber): Unsubscribe {
  return client.bindings.subscribeTotalUnread(cb);
}

/* ── All Unreads activity view (#1866) ──────────────────────────── */

/**
 * One room's unread events, projected for the All Unreads view.
 *
 * `events` are the message-like events newer than this room's read
 * marker, ordered oldest → newest so the view reads as a small
 * chronological digest per room. `lastActivityTs` mirrors the room
 * summary so the caller can sort groups by recency without re-walking
 * events.
 *
 * `unreadCount` is the rendered count — `events.length` — not the
 * raw server `notification_count`. The two diverge because the digest
 * filters out thread replies and state events, both of which the
 * server still counts toward push notifications. Using the rendered
 * length here keeps the room-header chip and the sidebar aggregate
 * badge agreeing with what the user actually sees in the list.
 *
 * `highlightCount` is the server-supplied mention count. It still
 * reflects the underlying unread state (the chip-vs-row reconciliation
 * is purely a UX decision) so the chip can colour itself for mentions
 * even when the rendered list is shorter than the server count.
 */
export interface UnreadGroup {
  roomId: string;
  roomName: string;
  /** True when the room is a direct message. The view doesn't split
   *  DMs and channels into separate sections — but the room-header
   *  glyph differs, so consumers need the bit. */
  isDirect: boolean;
  /** Rendered count — equal to `events.length`. The sidebar badge
   *  sums these so the badge total and the row total agree. */
  unreadCount: number;
  /** Highlight (mention) count from the server. May be capped by
   *  `unreadCount` so it never exceeds the rendered count, but
   *  preserves the boolean "has mentions" signal for chip styling. */
  highlightCount: number;
  /** Most-recent event timestamp in this group; fed by the room
   *  summary's `lastActivityTs` so groups sort consistently with
   *  the sidebar. */
  lastActivityTs: number;
  events: TimelineEvent[];
}

export type UnreadGroupsSubscriber = (groups: UnreadGroup[]) => void;

export function isUnreadTimelineEvent(event: TimelineEvent): boolean {
  if (isThreadReply(event)) return false;
  switch (event.type) {
    case "m.text":
    case "m.image":
    case "m.video":
    case "m.file":
    case "m.audio":
    case "m.emote":
    case "m.notice":
      return true;
    default:
      return false;
  }
}

/**
 * Subscribe to the All Unreads digest.
 *
 * Composes existing primitives — `subscribeRooms` for the
 * room set + `subscribeTimeline` for each room with `unreadTotal > 0`
 * — so this hook stays thin and the room-level data stays in one
 * place. The subscriber fires with an empty list synchronously, then
 * once per affected room as inner timelines deliver their snapshots,
 * then on every change (new event, read receipt clearing the count,
 * room joining / leaving).
 *
 * Matrix's per-room `m.fully_read` marker is the source of truth: the
 * digest shows only message-like events newer than that event id. During
 * cold start, or when the marker is not present in the current buffer,
 * it falls back to the room's server unread count to keep the digest
 * useful until the marker is available.
 */
export function subscribeUnreadTimeline(client: Client, cb: UnreadGroupsSubscriber): Unsubscribe {
  // Per-room timeline subscriptions, indexed by roomId so we can tear
  // down the ones that drop out of scope (room left the org, count
  // cleared, room left entirely).
  const innerUnsubs = new Map<string, Unsubscribe>();
  const markerUnsubs = new Map<string, Unsubscribe>();
  // Most recent timeline snapshot per room. Used to recompute the
  // grouped output whenever any input — rooms list or any inner
  // timeline — changes.
  const eventsByRoom = new Map<string, TimelineEvent[]>();
  const markersByRoom = new Map<string, string | null>();
  // Most recent room summary snapshot with `unreadTotal > 0`.
  let unreadRooms: RoomSummary[] = [];
  let cancelled = false;

  // Suppress nested emits while we set up the inner subscriptions for
  // a new rooms-list snapshot. Each `subscribeTimeline` call below
  // fires its initial snapshot synchronously, which would otherwise
  // emit the digest once per newly-added room before the outer
  // `subscribeRooms` callback's final flush. One `cb` per logical
  // change is what consumers expect.
  let suppressEmit = false;

  const emit = () => {
    if (cancelled) return;
    if (suppressEmit) return;
    const groups: UnreadGroup[] = [];
    for (const room of unreadRooms) {
      const buffer = eventsByRoom.get(room.id) ?? [];
      const marker = markersByRoom.get(room.id) ?? null;
      const markerIndex = marker ? buffer.findIndex((event) => event.id === marker) : -1;
      const unread =
        markerIndex >= 0 ? buffer.slice(markerIndex + 1).filter(isUnreadTimelineEvent) : [];
      if (markerIndex < 0) {
        const messages = buffer.filter(isUnreadTimelineEvent);
        unread.push(
          ...(room.unreadTotal > 0 && messages.length > 0
            ? messages.slice(-room.unreadTotal)
            : messages),
        );
      }
      // Drop groups with nothing to render. The server-side
      // notification count may be non-zero (e.g. only state events
      // were unread, or only thread replies) but the view has nothing
      // to show, and the bulk read path would have no event id to
      // target. The user clears those rooms by visiting them
      // directly — same trade-off as Element's "all" view.
      if (unread.length === 0) continue;
      // Cap highlight count to the rendered count so the chip can't
      // claim more mentions than it shows; preserves the
      // "has mentions" boolean signal for chip styling.
      const highlightCount = Math.min(room.unreadHighlight, unread.length);
      groups.push({
        roomId: room.id,
        roomName: room.name,
        isDirect: room.isDirect,
        unreadCount: unread.length,
        highlightCount,
        lastActivityTs: room.lastActivityTs,
        events: unread,
      });
    }
    // Newest first by room activity; matches the sidebar order and
    // means the room with the freshest unread sits at the top of the
    // digest.
    groups.sort((a, b) => b.lastActivityTs - a.lastActivityTs);
    cb(groups);
  };

  const unsubRooms = client.bindings.subscribeRooms((rooms) => {
    if (cancelled) return;
    // Has unreads. A muted room still appears here — the
    // user's mute decision applies to push notifications, not to
    // whether the message exists. The view is "show me everything I
    // haven't read"; mute-aware filtering belongs at the row level
    // (a future ticket can dim muted groups).
    const next = rooms.filter((r) => r.unreadTotal > 0);
    unreadRooms = next;
    const live = new Set(next.map((r) => r.id));

    // Tear down subscriptions for rooms that dropped out of scope.
    for (const [roomId, unsub] of innerUnsubs) {
      if (live.has(roomId)) continue;
      unsub();
      innerUnsubs.delete(roomId);
      const markerUnsub = markerUnsubs.get(roomId);
      if (markerUnsub) markerUnsub();
      markerUnsubs.delete(roomId);
      eventsByRoom.delete(roomId);
      markersByRoom.delete(roomId);
    }
    // Spin up a subscription for each newly-in-scope room. The
    // initial-snapshot emits inner timelines fire synchronously are
    // suppressed — we flush exactly once at the bottom so a rooms
    // update with N new rooms produces 1 callback to `cb`, not 1+N.
    suppressEmit = true;
    try {
      for (const room of next) {
        if (innerUnsubs.has(room.id)) continue;
        const unsub = client.bindings.subscribeTimeline(room.id, (events) => {
          if (cancelled) return;
          eventsByRoom.set(room.id, events);
          emit();
        });
        innerUnsubs.set(room.id, unsub);
        const markerUnsub = client.bindings.subscribeReadMarker(room.id, (eventId) => {
          if (cancelled) return;
          markersByRoom.set(room.id, eventId);
          emit();
        });
        markerUnsubs.set(room.id, markerUnsub);
      }
    } finally {
      suppressEmit = false;
    }
    emit();
  });

  return () => {
    cancelled = true;
    unsubRooms();
    for (const unsub of innerUnsubs.values()) unsub();
    for (const unsub of markerUnsubs.values()) unsub();
    innerUnsubs.clear();
    markerUnsubs.clear();
    eventsByRoom.clear();
    markersByRoom.clear();
  };
}

/* ── Threads activity view (#1868) ─────────────────────────────── */

const THREADS_FEED_CAP = 100;
const THREADS_ROOT_LOAD_CONCURRENCY = 4;
const THREADS_FEED_SETTLE_TIMEOUT_MS = 8000;

function eventBodyPreview(event: TimelineEvent): string {
  switch (event.content.type) {
    case "m.text":
    case "m.notice":
      return replacementBody(event.content);
    case "m.emote":
      return `* ${event.sender.displayName || event.sender.id} ${replacementBody(event.content)}`;
    case "m.image":
      return replacementBody(event.content) || "Image";
    case "m.video":
      return replacementBody(event.content) || "Video";
    case "m.file":
      return replacementBody(event.content) || "File";
    case "m.audio":
      return replacementBody(event.content) || "Audio";
    default:
      return "Message";
  }
}

function isMessageLikeThreadRoot(event: TimelineEvent): boolean {
  if (isThreadReply(event)) return false;
  switch (event.type) {
    case "m.text":
    case "m.image":
    case "m.video":
    case "m.file":
    case "m.audio":
    case "m.emote":
    case "m.notice":
      return true;
    default:
      return false;
  }
}

function findThreadRoots(events: TimelineEvent[]): TimelineEvent[] {
  const replyRootIds = new Set<string>();
  for (const event of events) {
    const rootId = event.threadRelation?.event_id;
    if (rootId) replyRootIds.add(rootId);
  }
  return events.filter(
    (event) =>
      isMessageLikeThreadRoot(event) &&
      ((event.threadSummary?.replyCount ?? 0) > 0 || replyRootIds.has(event.id)),
  );
}

function eventMentionsUser(event: TimelineEvent, userId: string | null): boolean {
  if (!userId) return false;
  const body = eventBodyPreview(event).toLowerCase();
  const id = userId.toLowerCase();
  const localpart = id.startsWith("@") ? (id.slice(1).split(":")[0] ?? "") : id;
  if (body.includes(id)) return true;
  if (localpart.length === 0) return false;
  const escaped = localpart.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9_])@${escaped}(?=$|[^a-z0-9_])`).test(body);
}

/**
 * Subscribe to every thread the current user participates in across
 * joined rooms. Server history supplies participated threads; the live
 * buffer also includes roots and replies the user sent or was mentioned
 * in. Output is capped to the 100 most-recent threads.
 */
export function subscribeThreadsFeed(client: Client, cb: ThreadsFeedSubscriber): Unsubscribe {
  const timelineUnsubs = new Map<string, Unsubscribe>();
  const threadUnsubs = new Map<string, Unsubscribe>();
  const liveRootsByRoom = new Map<string, TimelineEvent[]>();
  const serverRootsByRoom = new Map<string, TimelineEvent[]>();
  const serverSeededKeys = new Set<string>();
  const liveParticipatingKeys = new Set<string>();
  const serverRootLoads = new Set<string>();
  const serverRootQueue: RoomSummary[] = [];
  let activeServerRootLoads = 0;
  const seenThreadReplyIds = new Map<string, Set<string>>();
  const repliesByRoot = new Map<string, TimelineEvent[]>();
  const latestThreadActivityByRoot = new Map<string, number>();
  let feedRooms: RoomSummary[] = [];
  let cancelled = false;
  let suppressEmit = false;
  let settled = false;
  let roomsDelivered = false;
  let roomsInitialized = false;
  const threadDelivered = new Set<string>();

  const rootKey = (roomId: string, rootEventId: string) => `${roomId}::${rootEventId}`;

  const currentCandidates = (): Array<{ room: RoomSummary; root: TimelineEvent; key: string }> => {
    const candidates: Array<{ room: RoomSummary; root: TimelineEvent; key: string }> = [];
    for (const room of feedRooms) {
      const roots = new Map<string, TimelineEvent>();
      for (const root of serverRootsByRoom.get(room.id) ?? []) roots.set(root.id, root);
      for (const root of liveRootsByRoom.get(room.id) ?? []) roots.set(root.id, root);
      for (const root of roots.values()) {
        const key = rootKey(room.id, root.id);
        if (!serverSeededKeys.has(key) && !liveParticipatingKeys.has(key)) continue;
        candidates.push({ room, root, key });
      }
    }
    candidates.sort((a, b) => {
      const aTs = latestThreadActivityByRoot.get(a.key) ?? a.root.ts;
      const bTs = latestThreadActivityByRoot.get(b.key) ?? b.root.ts;
      return bTs - aTs;
    });
    return candidates.slice(0, THREADS_FEED_CAP);
  };

  const maybeSettle = () => {
    if (settled) return;
    if (!roomsDelivered || !roomsInitialized) return;
    const everyThreadBatchArrived = [...threadUnsubs.keys()].every((key) =>
      threadDelivered.has(key),
    );
    if (serverRootQueue.length === 0 && activeServerRootLoads === 0 && everyThreadBatchArrived) {
      settled = true;
    }
  };

  const emit = () => {
    if (cancelled || suppressEmit) return;
    maybeSettle();
    const userId = client.bindings.getUserId();
    const items: ThreadsFeedItem[] = [];
    for (const { room, root, key } of currentCandidates()) {
      const replies = repliesByRoot.get(key) ?? [];
      const lastReply = replies[replies.length - 1] ?? null;
      const participates =
        serverSeededKeys.has(key) ||
        liveParticipatingKeys.has(key) ||
        root.sender.id === userId ||
        eventMentionsUser(root, userId) ||
        replies.some((reply) => reply.sender.id === userId || eventMentionsUser(reply, userId));
      if (!participates) continue;
      const last = lastReply ?? root;
      const readReceiptEventId = client.bindings.getThreadReadReceipt(room.id, root.id);
      const readIndex = readReceiptEventId
        ? replies.findIndex((reply) => reply.id === readReceiptEventId)
        : -1;
      let lastOwnIndex = -1;
      replies.forEach((reply, index) => {
        if (reply.sender.id === userId) lastOwnIndex = index;
      });
      const unread = replies
        .slice(Math.max(readIndex, lastOwnIndex) + 1)
        .filter((reply) => reply.sender.id !== userId);
      const unreadReplyCount = unread.length;
      items.push({
        roomId: room.id,
        roomName: room.name,
        rootEventId: root.id,
        rootSender: root.sender.displayName || root.sender.id,
        rootBodyPreview: eventBodyPreview(root),
        lastReplySender: last.sender.displayName || last.sender.id,
        lastReplyBodyPreview: eventBodyPreview(last),
        lastReplyEventId: last.id,
        lastReplyTs: latestThreadActivityByRoot.get(key) ?? last.ts,
        replyCount: replies.length || root.threadSummary?.replyCount || 0,
        unreadReplyCount,
        unreadReplyEventIds: unread.map((reply) => reply.id),
        rootEvent: root,
      });
    }
    items.sort((a, b) => b.lastReplyTs - a.lastReplyTs);
    cb(items, { settled });
  };

  const reconcileThreadSubscriptions = () => {
    const knownRoots = new Set<string>();
    for (const room of feedRooms) {
      for (const root of serverRootsByRoom.get(room.id) ?? []) {
        knownRoots.add(rootKey(room.id, root.id));
      }
      for (const root of liveRootsByRoom.get(room.id) ?? []) {
        knownRoots.add(rootKey(room.id, root.id));
      }
    }
    for (const key of latestThreadActivityByRoot.keys()) {
      if (!knownRoots.has(key)) latestThreadActivityByRoot.delete(key);
    }
    for (const key of liveParticipatingKeys) {
      if (!knownRoots.has(key)) liveParticipatingKeys.delete(key);
    }
    const wanted = new Set<string>();
    for (const candidate of currentCandidates()) wanted.add(candidate.key);

    for (const [key, unsub] of threadUnsubs) {
      if (wanted.has(key)) continue;
      unsub();
      threadUnsubs.delete(key);
      repliesByRoot.delete(key);
    }

    for (const { room, root, key } of currentCandidates()) {
      if (threadUnsubs.has(key)) continue;
      const unsub = client.bindings.subscribeThread(room.id, root.id, (replies) => {
        if (cancelled) return;
        repliesByRoot.set(key, replies);
        latestThreadActivityByRoot.set(
          key,
          Math.max(
            root.threadSummary?.latestReplyTs ?? root.ts,
            replies[replies.length - 1]?.ts ?? 0,
          ),
        );
        threadDelivered.add(key);
        emit();
      });
      threadUnsubs.set(key, unsub);
    }
  };

  const pumpServerRootLoads = () => {
    while (activeServerRootLoads < THREADS_ROOT_LOAD_CONCURRENCY) {
      const room = serverRootQueue.shift();
      if (!room) return;
      if (!feedRooms.some((candidate) => candidate.id === room.id)) continue;
      activeServerRootLoads += 1;
      void client.bindings
        .listThreadRoots(room.id, undefined, THREADS_FEED_CAP)
        .then(({ roots }) => {
          if (cancelled || !feedRooms.some((candidate) => candidate.id === room.id)) return;
          const messageRoots = roots.filter(isMessageLikeThreadRoot);
          for (const key of serverSeededKeys) {
            if (key.startsWith(`${room.id}::`)) serverSeededKeys.delete(key);
          }
          serverRootsByRoom.set(room.id, messageRoots);
          for (const root of messageRoots) {
            const key = rootKey(room.id, root.id);
            serverSeededKeys.add(key);
            latestThreadActivityByRoot.set(
              key,
              Math.max(
                latestThreadActivityByRoot.get(key) ?? 0,
                root.threadSummary?.latestReplyTs ?? root.ts,
              ),
            );
          }
          reconcileThreadSubscriptions();
          emit();
        })
        .catch(() => undefined)
        .finally(() => {
          serverRootLoads.delete(room.id);
          activeServerRootLoads -= 1;
          pumpServerRootLoads();
          emit();
        });
    }
  };

  const loadServerRoots = (room: RoomSummary) => {
    if (serverRootLoads.has(room.id)) return;
    serverRootLoads.add(room.id);
    serverRootQueue.push(room);
    pumpServerRootLoads();
  };

  const unsubRoomListInit = client.bindings.subscribeRoomListInitialized(() => {
    if (cancelled) return;
    roomsInitialized = true;
    emit();
  });

  const unsubRooms = client.bindings.subscribeRooms((rooms) => {
    if (cancelled) return;
    roomsDelivered = true;
    feedRooms = rooms.filter((room) => !room.isSpace && room.archived === null);
    const liveRoomIds = new Set(feedRooms.map((room) => room.id));

    for (const [roomId, unsub] of timelineUnsubs) {
      if (liveRoomIds.has(roomId)) continue;
      unsub();
      timelineUnsubs.delete(roomId);
      liveRootsByRoom.delete(roomId);
      serverRootsByRoom.delete(roomId);
      serverRootLoads.delete(roomId);
      for (const key of serverSeededKeys) {
        if (key.startsWith(`${roomId}::`)) serverSeededKeys.delete(key);
      }
      for (const key of liveParticipatingKeys) {
        if (key.startsWith(`${roomId}::`)) liveParticipatingKeys.delete(key);
      }
      for (const key of latestThreadActivityByRoot.keys()) {
        if (key.startsWith(`${roomId}::`)) latestThreadActivityByRoot.delete(key);
      }
    }

    suppressEmit = true;
    try {
      for (const room of feedRooms) {
        loadServerRoots(room);
        if (timelineUnsubs.has(room.id)) continue;
        const unsub = client.bindings.subscribeTimeline(room.id, (events) => {
          if (cancelled) return;
          const roots = findThreadRoots(events);
          liveRootsByRoom.set(room.id, roots);
          const currentUserId = client.bindings.getUserId();
          for (const root of roots) {
            const key = rootKey(room.id, root.id);
            const participates =
              root.sender.id === currentUserId ||
              eventMentionsUser(root, currentUserId) ||
              events.some(
                (event) =>
                  event.threadRelation?.event_id === root.id &&
                  (event.sender.id === currentUserId || eventMentionsUser(event, currentUserId)),
              );
            if (participates) liveParticipatingKeys.add(key);
            const latestLiveReplyTs = events.reduce(
              (latest, event) =>
                event.threadRelation?.event_id === root.id ? Math.max(latest, event.ts) : latest,
              0,
            );
            const serverRoot = (serverRootsByRoom.get(room.id) ?? []).find(
              (candidate) => candidate.id === root.id,
            );
            latestThreadActivityByRoot.set(
              key,
              Math.max(
                latestLiveReplyTs,
                root.threadSummary?.latestReplyTs ?? root.ts,
                serverRoot?.threadSummary?.latestReplyTs ?? 0,
              ),
            );
          }
          reconcileThreadSubscriptions();
          emit();
          const seen = seenThreadReplyIds.get(room.id) ?? new Set<string>();
          let newThreadReply = false;
          for (const event of events) {
            if (event.threadRelation?.rel_type === THREAD_REL_TYPE && event.id) {
              if (seen.has(event.id)) continue;
              seen.add(event.id);
              newThreadReply = true;
            }
          }
          seenThreadReplyIds.set(room.id, seen);
          if (newThreadReply) loadServerRoots(room);
        });
        timelineUnsubs.set(room.id, unsub);
      }
      reconcileThreadSubscriptions();
    } finally {
      suppressEmit = false;
    }
    emit();
  });

  const settleCap = setTimeout(() => {
    if (cancelled) return;
    settled = true;
    emit();
  }, THREADS_FEED_SETTLE_TIMEOUT_MS);

  return () => {
    cancelled = true;
    clearTimeout(settleCap);
    unsubRoomListInit();
    unsubRooms();
    for (const unsub of timelineUnsubs.values()) unsub();
    for (const unsub of threadUnsubs.values()) unsub();
    timelineUnsubs.clear();
    threadUnsubs.clear();
    liveRootsByRoom.clear();
    serverRootsByRoom.clear();
    serverSeededKeys.clear();
    liveParticipatingKeys.clear();
    serverRootLoads.clear();
    serverRootQueue.length = 0;
    repliesByRoot.clear();
    latestThreadActivityByRoot.clear();
  };
}

/* ── Search ─────────────────────────────────────────────────────── */

/**
 * Run a full-text search against the homeserver. Thin wrapper over the
 * binding; exposed as a module function so consumers never reach
 * through `client.bindings` directly. An empty / whitespace-only
 * `query` short-circuits with an empty page — no reason to pay a
 * network round-trip for a query the server is guaranteed to reject.
 *
 * Binding errors bubble up. The consumer hook (`useSearch`) currently
 * treats failures as an empty result — same-shape rendering avoids a
 * mid-type error state that would strobe into view on transient
 * failures. A later ticket can surface an inline "retry" affordance
 * at the Panel level; that's a UI decision, not a wrapper one.
 */
export async function searchEvents(
  client: Client,
  query: string,
  roomId?: string,
  nextBatch?: string,
): Promise<SearchResultsPage> {
  if (typeof query !== "string" || query.trim().length === 0) {
    return { results: [], nextBatch: null };
  }
  return client.bindings.searchEvents(query, roomId, nextBatch);
}

/* ── Bindings loader ────────────────────────────────────────────── */

async function loadBindings(homeserverUrl: string): Promise<SdkBindings> {
  // Stories and tests stay on the deterministic stub. Every real runtime
  // must use the Rust SDK WASM binding; do not add partial HTTP/REST
  // fallbacks here, because they let the shell boot into a runtime that
  // cannot satisfy the SdkBindings contract.
  if (
    import.meta.env.MODE === "test" ||
    (import.meta.env.DEV &&
      import.meta.env.MODE !== "embed" &&
      import.meta.env.VITE_CHAT_MATRIX_BINDING === "stub")
  ) {
    const { createStubBindings } = await import("~/lib/matrix-sdk-stub");
    return createStubBindings(homeserverUrl);
  }
  // Mobile builds (`vite build --mode mobile`) run against the native Tauri
  // host instead of the wasm SDK; the wasm package is never imported here so
  // it stays out of the mobile bundle.
  if (import.meta.env.VITE_CHAT_MATRIX_BINDING === "native") {
    const { createNativeBindings } = await import("~/lib/matrix-sdk-native");
    return createNativeBindings(homeserverUrl);
  }
  const rust = await import("~/lib/matrix-sdk-rust").catch((cause) => {
    if (import.meta.env.DEV) {
      throw new Error(
        "Matrix WASM binding failed to load. Build it with `cd apps/chat/matrix-wasm && wasm-pack build --release --target web`, or set VITE_CHAT_MATRIX_BINDING=stub for UI-only dev.",
        { cause },
      );
    }
    throw cause;
  });
  return rust.createRustBindings(homeserverUrl);
}
